import React, { useEffect, useRef, useState } from "react";
import { useTable, useSortBy } from "react-table";
// import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { Modal, Spinner, OverlayTrigger, Popover } from "react-bootstrap";
import Subscription from "./Subscription";
import { useAlert } from "react-alert";
import "../../css/tableComponentCss.css";
import "./tableView.css";

// Sequential loading system for Reports consumed column - one by one

// Utility function to get previous month information
const getPreviousMonthInfo = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  // Calculate previous month
  let prevYear = currentYear;
  let prevMonth = currentMonth - 1;

  // Handle year rollover
  if (prevMonth < 0) {
    prevMonth = 11;
    prevYear = currentYear - 1;
  }

  const prevMonthStart = new Date(prevYear, prevMonth, 1);
  const prevMonthEnd = new Date(prevYear, prevMonth + 1, 0);

  return {
    startDate: prevMonthStart.toISOString().split('T')[0],
    endDate: prevMonthEnd.toISOString().split('T')[0],
    displayDate: `${prevMonthEnd.getDate().toString().padStart(2, '0')}-${(prevMonthEnd.getMonth() + 1).toString().padStart(2, '0')}-${prevMonthEnd.getFullYear().toString().slice(-2)}`
  };
};

// Utility function to get current month first date as epoch timestamp (local timezone)
const getCurrentMonthStartEpoch = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  // Set to 00:00:00 local time
  firstDayOfMonth.setHours(0, 0, 0, 0);
  return firstDayOfMonth.getTime();
};

// Utility function to get current date as epoch timestamp (local timezone)
const getCurrentDateEpoch = () => {
  return Date.now();
};

const formatFromDateTimeString = (date) => {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} 00:00:00.000`;
};

const formatToDateTimeString = (date) => {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} 23:59:59.999`;
};

const getCurrentMonthRangeDateTimes = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startDate: formatFromDateTimeString(start),
    endDate: formatToDateTimeString(now),
  };
};

const getClientCompositeKey = (client) => {
  if (!client) return "";
  const clientId = (client.clientId || "").toString();
  const org = (client.org || "").toString();
  const center = (client.center || "").toString();
  return `${clientId}|${org}|${center}`;
};

const LOW_USAGE_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const LOW_USAGE_NEGATIVE_CACHE_TTL_MS = 2 * 60 * 1000;
let lowUsageRequestQueue = Promise.resolve();
const lowUsageInFlight = new Map();

// Master list of all known add-ons — used as a fallback so the filter always
// shows these options even if no client currently has them assigned.
const MASTER_ADDONS = [
  "Cover Page",
  "Body Summary",
  "Test Recommendation",
  "Quick Summary",
  "Diet Page",
  "Consolidated Page",
  "Doctors Page",
  "Marketing Page",
  "Historical",
  "Viz App",
  "Smart Video Report",
  "Corporate Dashboard",
];

const isLiveSubscriptionRow = (client) => {
  const status = (client && client.subscriptionStatus ? client.subscriptionStatus : "")
    .toString()
    .trim()
    .toLowerCase();
  return status === "live";
};

const loadLowUsageCache = () => {
  try {
    const raw = localStorage.getItem("clientLowUsageCache");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const data = parsed && parsed.data ? parsed.data : parsed;
    return data && typeof data === "object" ? data : null;
  } catch (_) {
    return null;
  }
};

const saveLowUsageCache = (key, value, isFallback = false) => {
  try {
    const existing = loadLowUsageCache() || {};
    existing[key] = { value, ts: Date.now(), isFallback: !!isFallback };
    localStorage.setItem(
      "clientLowUsageCache",
      JSON.stringify({ data: existing, timestamp: Date.now() })
    );
  } catch (_) { }
};

const getLowUsageCacheValue = (key) => {
  const cached = loadLowUsageCache();
  const entry = cached && cached[key] ? cached[key] : null;
  if (!entry) return null;
  const ts = typeof entry.ts === "number" ? entry.ts : 0;
  const isFallback = !!entry.isFallback;
  const ttl = isFallback ? LOW_USAGE_NEGATIVE_CACHE_TTL_MS : LOW_USAGE_CACHE_TTL_MS;
  if (!ts || Date.now() - ts > ttl) return null;
  return entry.value || null;
};

const computeLowUsageWarning = (arr) => {
  // If today is the 1st of the month, yesterday was in the previous month.
  // It won't exist in the current month's data, so skip to avoid false warnings.
  if (new Date().getDate() === 1) {
    return { avg: 0, yesterdayCount: 0, threshold: 0, isWarning: false, isNoAverage: false, isZeroYesterday: false, isLowUsage: false, isCritical: false };
  }

  const total = (arr || []).reduce((sum, x) => {
    const n = Number(x && x.count);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const days = Array.isArray(arr) ? arr.length : 0;
  const avg = total === 0 || days === 0 ? 0 : Math.round(total / days);

  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yKey = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(
    y.getDate()
  ).padStart(2, "0")}`;

  let yesterdayCount = 0;
  for (let i = 0; i < (arr || []).length; i++) {
    const row = arr[i];
    const raw = row && row.date ? row.date : "";
    const d = raw.toString().split("T")[0].split(" ")[0];
    if (d === yKey) {
      const n = Number(row && row.count);
      yesterdayCount = Number.isFinite(n) ? n : 0;
      break;
    }
  }

  const threshold = avg * 0.5;

  // New alert type flags
  const isNoAverage = avg === 0; // No reports this month
  const isZeroYesterday = avg > 0 && yesterdayCount === 0; // Zero reports yesterday
  const isLowUsage = avg > 0 && yesterdayCount > 0 && yesterdayCount < threshold; // Existing logic

  // Critical alerts (RED) for no average or zero yesterday
  const isCritical = isNoAverage || isZeroYesterday;

  // Show warning if any alert condition is met
  const isWarning = isNoAverage || isZeroYesterday || isLowUsage;

  return { avg, yesterdayCount, threshold, isWarning, isNoAverage, isZeroYesterday, isLowUsage, isCritical };
};

const enqueueLowUsageRequest = (task) => {
  lowUsageRequestQueue = lowUsageRequestQueue
    .then(async () => {
      const result = await task();
      await new Promise((r) => setTimeout(r, 150));
      return result;
    })
    .catch(async () => {
      await new Promise((r) => setTimeout(r, 150));
      return null;
    });
  return lowUsageRequestQueue;
};
const CustomFilterDropdown = ({ value, options, onChange, title, width = "120px", listWidth = "140px" }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: width }}>
      <div
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        style={{
          fontSize: "11px",
          padding: "2px 20px 2px 6px",
          borderRadius: "5px",
          border: "1px solid #dde0e1",
          background: "#f8f9fa",
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          position: "relative",
          textAlign: "left",
          color: "#334155",
          height: "22px",
          display: "flex",
          alignItems: "center"
        }}
        title={value || title}
      >
        {value || "All"}
        <span style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", fontSize: "8px", color: "#94a3b8" }}>
          ▼
        </span>
      </div>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: listWidth,
            maxHeight: "150px",
            overflowY: "auto",
            border: "1px solid #dde0e1",
            borderRadius: "6px",
            background: "#fff",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 9999,
            scrollbarWidth: "thin",
            marginTop: "4px",
            textAlign: "left"
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((addon) => {
            const isActive = value === addon;
            return (
              <div
                key={addon || "__all__"}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(addon);
                  setIsOpen(false);
                }}
                title={addon || "All"}
                style={{
                  padding: "6px 8px",
                  fontSize: "11px",
                  cursor: "pointer",
                  backgroundColor: isActive ? "#e0f2fe" : "transparent",
                  color: isActive ? "#0369a1" : "#334155",
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  borderBottom: "1px solid #f1f5f9"
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "#f8fafc"; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                {addon || "All"}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const getLowUsageInfoForClient = async (client, options = {}) => {
  const key = getClientCompositeKey(client);
  if (!key) return { isWarning: false };

  if (!isLiveSubscriptionRow(client)) {
    return { isWarning: false };
  }

  const forceRefresh = !!(options && options.forceRefresh);

  const cachedValue = forceRefresh ? null : getLowUsageCacheValue(key);
  if (cachedValue) return cachedValue;

  const existingPromise = lowUsageInFlight.get(key);
  if (existingPromise) return existingPromise;

  const promise = enqueueLowUsageRequest(async () => {
    const idToken = localStorage.getItem("idTokenBilling");
    const clientId = client && client.clientId ? client.clientId : "";
    const org = client && client.org ? client.org : "";
    const center = client && client.center ? client.center : "";
    if (!idToken || !clientId || !org || !center) return { isWarning: false };

    const { startDate, endDate } = getCurrentMonthRangeDateTimes();
    const url = `https://api.niroggyan.com/billing/smartreport/counts?query_on=counts&query_by=range&start=${encodeURIComponent(
      startDate
    )}&end=${encodeURIComponent(endDate)}&org=${encodeURIComponent(org)}&center=${encodeURIComponent(
      center
    )}&clientId=${encodeURIComponent(clientId)}`;

    const configBody = {
      headers: { authorization: idToken },
      method: "get",
      url,
      timeout: 25000,
      validateStatus: (status) => status >= 200 && status < 510,
    };

    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await axios(configBody);
        const payload = response && response.data ? response.data : null;
        const arr = payload && payload.data ? payload.data : null;
        const isSuccess = payload && Object.prototype.hasOwnProperty.call(payload, "isSuccess") ? !!payload.isSuccess : true;
        if (!isSuccess || !Array.isArray(arr) || (response && typeof response.status === "number" && response.status >= 500)) {
          const fallback = { isWarning: false };
          saveLowUsageCache(key, fallback, true);
          return fallback;
        }
        const computed = computeLowUsageWarning(arr);
        saveLowUsageCache(key, computed, false);
        return computed;
      } catch (_) {
        if (attempt === 0) {
          await new Promise((r) => setTimeout(r, 400));
        }
      }
    }

    const fallback = { isWarning: false };
    saveLowUsageCache(key, fallback, true);
    return fallback;
  }).finally(() => {
    lowUsageInFlight.delete(key);
  });

  lowUsageInFlight.set(key, promise);
  return promise;
};

const useLowUsageWarningInfo = (client, tick) => {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const key = getClientCompositeKey(client);

    if (!isLiveSubscriptionRow(client)) {
      setInfo({ isWarning: false });
      return () => {
        isMounted = false;
      };
    }

    (async () => {
      if (!key) return;
      try {
        const computed = await getLowUsageInfoForClient(client, { forceRefresh: !!tick });
        if (!isMounted) return;
        setInfo(computed);
      } catch (_) {
        if (!isMounted) return;
        setInfo({ isWarning: false });
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [client?.clientId, client?.org, client?.center, client?.subscriptionStatus, tick]);

  return info;
};

const LowUsageWarningIcon = ({ info }) => {
  if (!info || !info.isWarning) return null;

  const isCritical = !!(info && info.isCritical);

  // Red for critical (no average or zero yesterday), yellow/orange for low usage
  const bgColor = isCritical ? "rgba(220, 38, 38, 0.16)" : "rgba(245, 158, 11, 0.16)";
  const borderColor = isCritical ? "rgba(220, 38, 38, 0.55)" : "rgba(245, 158, 11, 0.55)";
  const strokeColor = isCritical ? "#DC2626" : "#B45309";

  return (
    <span
      aria-label={isCritical ? "Critical usage warning" : "Low usage warning"}
      style={{
        width: 18,
        height: 18,
        borderRadius: 6,
        background: bgColor,
        border: `1px solid ${borderColor}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        cursor: "default",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M12 9v4" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 17h.01" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
          stroke={strokeColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
};

const ClientNameCellWithLowUsageWarning = ({ client, customerName, isMobile, refreshTick }) => {
  const info = useLowUsageWarningInfo(client, refreshTick);
  const isWarning = !!(info && info.isWarning);
  const isCritical = !!(info && info.isCritical);

  // Determine popover header and message based on alert type
  let popoverHeader = "Low Usage Detected";
  let popoverMessage = `Yesterday's count (${info?.yesterdayCount}) was significantly lower than the daily average (${info?.avg}).`;

  if (info?.isNoAverage) {
    popoverHeader = "No Reports This Month";
    popoverMessage = "No reports generated this month.";
  } else if (info?.isZeroYesterday) {
    popoverHeader = "Zero Reports Yesterday";
    popoverMessage = "Zero reports generated yesterday.";
  }

  // Colors based on critical vs non-critical
  const bgColor = isCritical ? "rgba(220, 38, 38, 0.12)" : "rgba(245, 158, 11, 0.12)";
  const borderColor = isCritical ? "rgba(220, 38, 38, 0.25)" : "rgba(245, 158, 11, 0.25)";
  const textColor = isCritical ? "#991B1B" : "#92400e";

  const pop = (
    <Popover id={`low-usage-${getClientCompositeKey(client)}`}>
      <Popover.Header as="h3" style={{ fontSize: "14px", color: isCritical ? "#991B1B" : undefined }}>
        {popoverHeader}
      </Popover.Header>
      <Popover.Body style={{ fontSize: "13px", color: "#334155" }}>
        {popoverMessage}
      </Popover.Body>
    </Popover>
  );

  const content = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
        padding: isWarning ? "3px 8px" : 0,
        borderRadius: isWarning ? 10 : 0,
        background: isWarning ? bgColor : "transparent",
        border: isWarning ? `1px solid ${borderColor}` : "1px solid transparent",
      }}
    >
      <LowUsageWarningIcon info={info} />
      <div
        className="client-name-cell"
        style={{
          width: isMobile ? "100%" : "140px",
          maxWidth: isMobile ? "100%" : "140px",
          wordWrap: "break-word",
          fontSize: isMobile ? "12px" : "16px",
          overflowWrap: "break-word",
          paddingRight: isMobile ? undefined : "7px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: isMobile ? "normal" : "nowrap",
          lineHeight: "1.4",
          display: isMobile ? "-webkit-box" : "block",
          WebkitLineClamp: isMobile ? 2 : "unset",
          WebkitBoxOrient: isMobile ? "vertical" : "unset",
          color: isWarning ? textColor : undefined,
          fontWeight: isWarning ? 600 : undefined,
        }}
        title={customerName}
      >
        {customerName}
      </div>
    </div>
  );

  if (!isWarning) return content;

  return (
    <OverlayTrigger placement="right" overlay={pop} trigger={["hover", "focus"]}>
      {content}
    </OverlayTrigger>
  );
};

// Function to get client reports count (returns count string or throws error)
const getClientReportsCount = async (clientDetails) => {
  const idToken = localStorage.getItem("idTokenBilling");

  // Resolve userId from loginDetails using robust matching
  let clientData = [];
  try {
    clientData = JSON.parse(localStorage.getItem("loginDetails"))?.clientData || [];
  } catch (_) {
    clientData = [];
  }

  const norm = (v) => (v ?? "").toString().trim().toLowerCase();
  const orgKey = norm(clientDetails?.org);
  const centerKey = norm(clientDetails?.center);

  let userId = clientDetails?.userId; // fallback if present on row
  if (!userId && Array.isArray(clientData) && clientData.length > 0) {
    const matched = clientData.find((each) => norm(each.org) === orgKey && norm(each.center) === centerKey);
    userId = matched?.userId;
  }

  if (!userId || !idToken) throw new Error("Missing userId or idToken");

  const configBody = {
    headers: { authorization: idToken },
    method: "get",
    url: `https://api.niroggyan.com/billing/smartreport/counts?query_on=counts&query_by=subscription&userId=${userId}&cycle=`,
    validateStatus: (status) => status >= 200 && status < 510,
    timeout: 10000, // 10 second timeout
  };

  const response = await axios(configBody);

  if (response.data.isSuccess && response.data.data && response.data.data[0]) {
    const count = response.data.data[0].count;
    return count.toString();
  } else {
    throw new Error("No data returned from API");
  }
};

// Function to get client feedback rating (averageFeedback -> "X.X/5 (Y)" or "Not Available")
const getClientFeedback = async (clientDetails) => {
  const idToken = localStorage.getItem("idTokenBilling");

  // Resolve clientId from loginDetails using robust matching
  let clientData = [];
  try {
    clientData = JSON.parse(localStorage.getItem("loginDetails"))?.clientData || [];
  } catch (_) {
    clientData = [];
  }

  const norm = (v) => (v ?? "").toString().trim().toLowerCase();
  const orgKey = norm(clientDetails?.org);
  const centerKey = norm(clientDetails?.center);

  let clientId = clientDetails?.clientId; // fallback if present on row
  if (!clientId && Array.isArray(clientData) && clientData.length > 0) {
    const matched = clientData.find((each) => norm(each.org) === orgKey && norm(each.center) === centerKey);
    clientId = matched?.clientId;
  }

  if (!clientId || !idToken) throw new Error("Missing clientId or idToken");

  // Fixed date range: Jan 1, 2021 to current date (in ms epoch)
  const startEpoch = new Date(2021, 0, 1).getTime();
  const endEpoch = Date.now();

  const configBody = {
    headers: { authorization: idToken, "Content-Type": "application/json" },
    method: "get",
    url: `https://api.niroggyan.com/analytics/smartreport/feedback?clientId=${clientId}&start_epoch=${startEpoch}&end_epoch=${endEpoch}&filter_by=date_range`,
    validateStatus: (s) => s >= 200 && s < 510,
    timeout: 10000, // 10 second timeout
  };

  try {
    const response = await axios(configBody);
    const payload = response && response.data;
    const arr = payload && payload.data;

    if (Array.isArray(arr) && arr.length > 0) {
      const feedbackData = arr[0];
      const avg = feedbackData && feedbackData.averageFeedback;
      const total = feedbackData && feedbackData.totalFeedback;
      const avgNum = typeof avg === "number" ? avg : parseFloat(avg);
      const totalNum = typeof total === "number" ? total : parseInt(total);

      if (!isNaN(avgNum) && avgNum > 0) {
        // Return format: "X.X/5 (Y)" where Y is total feedback count
        const result = `${avgNum.toFixed(1)}/5 (${totalNum || 0})`;
        return result;
      }
    }

    return "Not Available";
  } catch (error) {
    throw error; // Re-throw to allow retry logic to work
  }
};

const getClientReportType = async (clientDetails) => {
  // Resolve userId from loginDetails using robust matching (org+center)
  let clientData = [];
  try {
    clientData = JSON.parse(localStorage.getItem("loginDetails"))?.clientData || [];
  } catch (_) {
    clientData = [];
  }

  const norm = (v) => (v ?? "").toString().trim().toLowerCase();
  const orgKey = norm(clientDetails?.org);
  const centerKey = norm(clientDetails?.center);

  let userId = clientDetails?.userId; // prefer userId from row
  if (!userId && Array.isArray(clientData) && clientData.length > 0) {
    const matched = clientData.find((each) => norm(each.org) === orgKey && norm(each.center) === centerKey);
    userId = matched?.userId;
  }

  if (!userId) throw new Error("Missing userId");

  const configBody = {
    headers: { "Content-Type": "application/json" },
    method: "get",
    url: `https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/subscription/status?userId=${userId}`,
    validateStatus: (s) => s >= 200 && s < 510,
    timeout: 10000, // 10 second timeout
  };

  try {
    const response = await axios(configBody);
    const data = response && response.data && response.data.data ? response.data.data : {};

    // Extract report type from the response
    const raw = ((data.reportSubscription && data.reportSubscription.reportType) || data.reportType || "").toString().trim().toLowerCase();
    const map = {
      compact: "Compact",
      dynamic: "Personal",
      advanced: "Total",
      personal: "Personal",
      total: "Total"
    };

    const reportType = map[raw] || "";

    if (reportType) {
      return reportType;
    }

    // No display string; return empty to avoid showing placeholders
    return "";
  } catch (error) {
    throw error; // Re-throw to allow retry logic to work
  }
};

// Function to get client activity count (returns count number or throws error)
const getActivityCount = async (clientDetails) => {
  const idToken = localStorage.getItem("idTokenBilling");

  if (!clientDetails || !clientDetails.clientId || !idToken) {
    // Return 0 if missing required data
    return 0;
  }

  const clientId = clientDetails.clientId;
  const startDate = getCurrentMonthStartEpoch();
  const endDate = getCurrentDateEpoch();

  const configBody = {
    headers: {
      authorization: idToken,
      "Content-Type": "application/json"
    },
    method: "get",
    url: `https://at8ed64ird.execute-api.ap-south-1.amazonaws.com/production/activity/user/logs?startDate=${startDate}&endDate=${endDate}&clientId=${clientId}&origin=https://www.client-dashboard.niroggyan.com&reportCounts=true`,
    validateStatus: (status) => status >= 200 && status < 510,
  };

  try {
    const response = await axios(configBody);
    
    // Log API details, payload, and response for each client as requested
    console.log(`--- Activity Log API for: ${clientDetails.customerName || clientDetails.org} ---`, {
      API: configBody.url.split('?')[0],
      Payload: {
        startDate,
        endDate,
        clientId,
        origin: "https://www.client-dashboard.niroggyan.com",
        reportCounts: true
      },
      Response: response.data
    });

    if (response.data.isSuccess && Array.isArray(response.data.data)) {
      // Return the length of the data array
      return response.data.data.length;
    } else {
      // If isSuccess is false or data is empty/invalid, return 0
      return 0;
    }
  } catch (error) {
    // On any error, return 0 instead of throwing
    return 0;
  }
};

// Lightweight per-row fetchers to show subscription fields in table






const CustomerNameCell = ({ client, tick }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    // Reset loading state when tick changes (refresh triggered)
    if (isMounted) setLoading(true);

    // Listen for global refresh events
    const handleGlobalRefresh = () => {
      if (isMounted) setLoading(true);
    };

    window.addEventListener('table-refresh', handleGlobalRefresh);

    const fetchData = async (retry = 0) => {
      try {
        if (isMounted) setLoading(true);
        const loginDetailsRaw = localStorage.getItem("loginDetails");
        if (!loginDetailsRaw) {
          if (isMounted) setValue("");
          return;
        }

        const clientData = JSON.parse(loginDetailsRaw).clientData || [];
        const selected = clientData.find(
          (each) => each.org === client.org && each.center === client.center
        );

        if (!selected) {
          if (isMounted) setValue("");
          return;
        }

        const url = `https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/subscription/status?userId=${selected.userId}`;
        const res = await axios({
          method: "GET",
          url,
          headers: { "Content-Type": "application/json" },
          timeout: 10000 // 10 second timeout
        });

        if (isMounted) {
          const customerName = res?.data?.data?.customerName;
          if (typeof customerName === "string" && customerName.trim()) {
            setValue(customerName.trim());
            setRetryCount(0);
          } else {
            setValue("");
          }
        }
      } catch (error) {

        if (isMounted && retry < 2) {
          // Retry after 1-3 seconds with exponential backoff
          const delay = Math.pow(2, retry) * 1000 + Math.random() * 1000;
          timeoutId = setTimeout(() => {
            if (isMounted) {
              setRetryCount(retry + 1);
              fetchData(retry + 1);
            }
          }, delay);
        } else if (isMounted) {
          setValue("");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('table-refresh', handleGlobalRefresh);
    };
  }, [client.org, client.center, retryCount, tick]);

  return <span>{loading ? "Loading..." : value}</span>;
};


const ReportTypeCell = ({ client, tick, onResolved }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchOnce = async () => {
      try {
        if (isMounted) setLoading(true);
        const reportType = await getClientReportType(client);
        if (isMounted) {
          setValue(reportType || "");
          if (typeof onResolved === "function" && reportType) {
            onResolved(reportType);
          }
        }
      } catch (_) {
        if (isMounted) setValue("");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOnce();
    return () => {
      isMounted = false;
    };
  }, [client.org, client.center, tick]);

  return (
    <span>
      {loading ? "Loading..." : value}
    </span>
  );
};

const FeedbackRatingCell = ({ client, tick, onResolved }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [hasValidData, setHasValidData] = useState(false);
  const [manualFetchTriggered, setManualFetchTriggered] = useState(false);

  // Check if this client is live
  const isLive = isLiveSubscriptionRow(client);

  // Helper function to generate composite key for this client
  const getFeedbackKey = (clientData) => {
    if (!clientData) return null;
    const clientId = clientData.clientId || "";
    const org = clientData.org || "";
    const center = clientData.center || "";
    return `${clientId}|${org}|${center}`;
  };

  // Helper functions for localStorage caching
  const loadFeedbackRatingsFromCacheLocal = () => {
    try {
      const cached = localStorage.getItem("feedbackRatingsCache");
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.data || parsed;
      }
    } catch (error) {
      console.warn("Error loading feedback ratings from cache:", error);
    }
    return {};
  };

  const saveFeedbackRatingsToCacheLocal = (key, rating) => {
    try {
      const cached = loadFeedbackRatingsFromCacheLocal();
      cached[key] = rating;
      const cacheData = {
        data: cached,
        timestamp: Date.now()
      };
      localStorage.setItem("feedbackRatingsCache", JSON.stringify(cacheData));
    } catch (error) {
      console.warn("Error saving feedback ratings to cache:", error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    let currentRequest = null;

    // Check localStorage cache first
    const feedbackKey = getFeedbackKey(client);
    const cachedRatings = loadFeedbackRatingsFromCacheLocal();
    const hasCachedData = feedbackKey && cachedRatings[feedbackKey];

    if (hasCachedData) {
      // Use cached value immediately
      const cachedValue = cachedRatings[feedbackKey];
      if (isMounted) {
        setValue(cachedValue);
        setHasValidData(true);
        setLoading(false);
        if (typeof onResolved === "function") onResolved(cachedValue);
      }
      // Don't fetch if we have cached data - cache persists across refreshes
      return () => {
        isMounted = false;
        if (timeoutId) clearTimeout(timeoutId);
        if (currentRequest) currentRequest.cancel?.();
      };
    }

    // STATUS-BASED LOADING:
    // Non-live clients (inactive/on-hold): DON'T auto-fetch, show "Load Data" button
    // Only fetch if client is live OR if manual fetch was triggered
    if (!isLive && !manualFetchTriggered) {
      if (isMounted) {
        setLoading(false);
        setValue("");
        setHasValidData(false);
      }
      return () => { isMounted = false; };
    }

    // Reset loading state when tick changes (refresh triggered)
    // But only if we don't have cached data
    if (isMounted) {
      setLoading(true);
      setValue("");
      setHasValidData(false);
    }

    const handleGlobalRefresh = () => {
      // Don't reset if we have cached data - cache persists
      // This ensures cache is not cleared on refresh
      if (isMounted && !hasCachedData) {
        setLoading(true);
        setValue("");
        setHasValidData(false);
      }
    };

    window.addEventListener('table-refresh', handleGlobalRefresh);

    const fetchData = async (retry = 0) => {
      // Cancel any existing request
      if (currentRequest) {
        currentRequest.cancel?.();
      }

      try {
        if (isMounted) setLoading(true);

        // Create a cancelable request
        const controller = new AbortController();
        currentRequest = { cancel: () => controller.abort() };

        const rating = await getClientFeedback(client);

        if (isMounted && !controller.signal.aborted) {
          // Only update if we got a valid rating (not "Not Available")
          if (rating && rating !== "Not Available" && rating !== "N/A") {
            const finalRating = rating;
            setValue(finalRating);
            setHasValidData(true);
            setRetryCount(0);
            // Save to cache after successful fetch
            if (feedbackKey) {
              saveFeedbackRatingsToCacheLocal(feedbackKey, finalRating);
            }
            if (typeof onResolved === "function") onResolved(finalRating);
          } else if (retry < 3) {
            // If we got "Not Available" but haven't retried much, retry
            const delay = Math.pow(2, retry) * 1000 + Math.random() * 1000;
            timeoutId = setTimeout(() => {
              if (isMounted) {
                setRetryCount(retry + 1);
                fetchData(retry + 1);
              }
            }, delay);
          } else {
            // After 3 retries, accept "Not Available"
            const finalRating = "Not Available";
            setValue(finalRating);
            setHasValidData(true);
            // Save "Not Available" to cache to avoid repeated retries
            if (feedbackKey) {
              saveFeedbackRatingsToCacheLocal(feedbackKey, finalRating);
            }
          }
        }
      } catch (error) {
        // Suppress console errors for expected failures (404, 502, timeouts)
        const isExpectedError =
          error?.response?.status === 404 || // Not Found - client doesn't have feedback data
          error?.response?.status === 502 || // Bad Gateway - server issues
          error?.code === 'ECONNABORTED'; // Timeout errors

        if (!isExpectedError) {
          console.error("Feedback API Error:", error);
        }

        if (isMounted && !currentRequest?.cancel) {
          // Only retry on actual errors, not cancellations
          if (retry < 2) { // Reduced retries for faster failure

            const delay = Math.pow(2, retry) * 1000 + Math.random() * 1000;
            timeoutId = setTimeout(() => {
              if (isMounted) {
                setRetryCount(retry + 1);
                fetchData(retry + 1);
              }
            }, delay);
          } else {
            // After retries, give up
            const finalRating = "Not Available";
            setValue(finalRating);
            setHasValidData(true);
            // Save "Not Available" to cache to avoid repeated API calls
            if (feedbackKey) {
              saveFeedbackRatingsToCacheLocal(feedbackKey, finalRating);
            }
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          currentRequest = null;
        }
      }
    };

    fetchData();

    return () => {
      window.removeEventListener('table-refresh', handleGlobalRefresh);
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (currentRequest) currentRequest.cancel?.();
    };
  }, [client.org, client.center, tick, manualFetchTriggered]);

  const isLoading = loading;
  const isNA = value === "Not Available" || value === "N/A";
  const hasValue = !!value && !isLoading && !isNA;

  // Show value pill when we have a non-loading value like "4.2/5 (2)"
  if (hasValue) {
    // Extract the rating part for color coding (e.g., "4.2" from "4.2/5 (2)")
    const ratingMatch = value.match(/(\d+\.?\d*)\/5/);
    const rating = ratingMatch ? parseFloat(ratingMatch[1]) : 0;

    return (
      <div
        style={{
          backgroundColor:
            rating >= 4
              ? "#D1FAE5"
              : rating >= 3
                ? "#FEF3C7"
                : "#FEE2E2",
          color:
            rating >= 4
              ? "#065F46"
              : rating >= 3
                ? "#92400E"
                : "#991B1B",
          borderRadius: "5px",
          border: "none",
          width: "100%",
          padding: "5px",
          textAlign: "center",
          fontWeight: "500"
        }}
      >
        {value}
      </div>
    );
  }

  // If explicitly Not Available, show a gray label
  if (isNA) {
    return (
      <div
        style={{
          backgroundColor: "#F3F4F6",
          color: "#6B7280",
          borderRadius: "5px",
          border: "none",
          width: "100%",
          padding: "5px",
          textAlign: "center",
          fontWeight: "500",
        }}
      >
        Not Available
      </div>
    );
  }

  // Non-live clients: show "Load Data" button if no data loaded and not currently fetching
  if (!isLive && !hasValidData && !isLoading) {
    return (
      <button
        className="btn btn-primary btn-sm w-100"
        style={{
          fontSize: "13px",
          padding: "4px 8px",
        }}
        onClick={() => {
          setManualFetchTriggered(true);
        }}
      >
        Load Data
      </button>
    );
  }

  // Show loading spinner
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        padding: "5px",
      }}
    >
      <Spinner animation="border" variant="primary" size="sm" />
    </div>
  );
};

const ReportsCountCell = ({ client, tick }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    // Reset loading state when tick changes (refresh triggered)
    if (isMounted) setLoading(true);

    const handleGlobalRefresh = () => {
      if (isMounted) setLoading(true);
    };

    window.addEventListener('table-refresh', handleGlobalRefresh);

    const fetchData = async (retry = 0) => {
      try {
        if (isMounted) setLoading(true);
        const count = await getClientReportsCount(client);

        if (isMounted) {
          setValue(count || "");
          setRetryCount(0);
        }
      } catch (error) {
        if (isMounted) {
          // Keep retrying indefinitely with exponential backoff
          const delay = Math.pow(2, retry) * 1000 + Math.random() * 1000;
          timeoutId = setTimeout(() => {
            if (isMounted) {
              setRetryCount(retry + 1);
              fetchData(retry + 1);
            }
          }, delay);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      window.removeEventListener('table-refresh', handleGlobalRefresh);
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [client.org, client.center, retryCount, tick]);

  const isLoading = loading;
  const hasValue = !!value && !isLoading;

  // Show value pill when we have a non-loading value
  if (hasValue) {
    const countNum = parseFloat(value);
    const isOverLimit = countNum > 0; // You can add comparison logic here if needed

    return (
      <div
        style={{
          backgroundColor: isOverLimit ? "#D1FFE9" : "#FFDBD7",
          borderRadius: "5px",
          border: "none",
          width: "100%",
          padding: "5px",
          textAlign: "center",
        }}
      >
        <span
          style={{
            color: isOverLimit ? "#2DAC6E" : "#E3695E",
          }}
        >
          {value}
        </span>
      </div>
    );
  }

  // Show loading spinner
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        padding: "5px",
      }}
    >
      <Spinner animation="border" variant="primary" size="sm" />
    </div>
  );
};

// Loading screen component
const LoadingScreen = ({ progress }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '40px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    margin: '20px'
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      border: '4px solid #e3e3e3',
      borderTop: '4px solid #007bff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '20px'
    }}></div>
    <h4 style={{ color: '#495057', marginBottom: '10px' }}>Loading Client Data...</h4>
    <div style={{
      width: '300px',
      height: '8px',
      backgroundColor: '#e3e3e3',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '10px'
    }}>
      <div style={{
        width: `${progress}%`,
        height: '100%',
        backgroundColor: '#007bff',
        transition: 'width 0.3s ease',
        borderRadius: '4px'
      }}></div>
    </div>
    <p style={{ color: '#6c757d', fontSize: '14px', margin: 0 }}>
      {progress}% Complete
    </p>
    <style jsx>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function Table({ columns, data, isMobile }) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn: {
          // Disable sorting for all columns by default
          disableSortBy: true,
        },
        autoResetSortBy: false,
        disableMultiSort: true,
        initialState: {
          // No sorting by default
          hiddenColumns: ["loader", "vizAppLoader", "prevCountLoader", "prevMonthLoader", "previousCount", "previousMonthCount", "org", "center", "mailIds", "vizAppCount"],
        },
      },
      useSortBy
    );

  // We don't want to render all 2000 rows for this example, so cap
  // it at 20 for this use case
  // const [rowIndex, setRowIndex] = useState(0);
  // const rowsToDisplay = 6;
  // const firstPageRows = rows.slice(
  //   rowIndex * rowsToDisplay,
  //   (rowIndex + 1) * rowsToDisplay
  // );

  return (
    <>
      <table {...getTableProps()} style={{
        position: "relative",
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        tableLayout: "auto"
      }}>
        <thead style={{
          // Sticky header layer (helps avoid sticky-left body cells drawing above headers in some browsers)
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
        }}>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers
                .filter((column) => !isMobile || column.showMobile)
                .map((column, colIndex) => {
                  const sortableIds = [
                    "customerName",
                    "startDate",
                    "endDate",
                    "liveDate",
                    "reportsSubscribed",
                    "feedbackRating",
                    "invoiceAmountCurrent",
                    "perReportRate",
                    "activityCount",
                  ]; // sortable columns
                  const isSortable = sortableIds.includes(column.id);
                  const isFirstColumn = colIndex === 0;
                  const headerProps = isSortable
                    ? { ...column.getHeaderProps(column.getSortByToggleProps()) }
                    : { ...column.getHeaderProps() };
                  const existingStyle = headerProps.style || {};
                  return (
                    <th
                      className={column.showMobile ? "" : "mobile-column"}
                      {...headerProps}
                      style={{
                        ...existingStyle,
                        cursor: isSortable ? "pointer" : "default",
                        background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                        position: "sticky",
                        top: 0,
                        left: isFirstColumn ? 0 : undefined,
                        // Ensure sticky headers always stay above sticky body cells during Y-scroll
                        zIndex: isFirstColumn ? 60 : 55,
                        boxShadow: isFirstColumn ? "2px 0 6px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)" : "0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
                        padding: "14px 12px",
                        fontWeight: "700",
                        fontSize: "13px",
                        color: "#1e293b",
                        borderBottom: "2px solid #cbd5e1",
                        borderRight: isFirstColumn ? "2px solid #cbd5e1" : "1px solid rgba(226, 232, 240, 0.8)",
                        textAlign: "left",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s ease",
                        ...(isSortable ? {
                          ":hover": {
                            background: "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)"
                          }
                        } : {})
                      }}
                      onMouseEnter={(e) => {
                        if (isSortable) {
                          e.currentTarget.style.background = "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isSortable) {
                          e.currentTarget.style.background = "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }
                      }}
                      title={isSortable ? (column.isSorted ? (column.isSortedDesc ? "Click to sort ascending" : "Click to sort descending") : "Click to sort") : undefined}
                    >
                      <span style={{
                        display: "inline-flex",
                        alignItems: column.id === "customerName" ? "flex-start" : "center",
                        gap: 8,
                        letterSpacing: "0.01em"
                      }}>
                        {column.render("Header")}
                        {isSortable && (
                          <span
                            aria-hidden="true"
                            style={{
                              display: "inline-flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                              marginTop: column.id === "customerName" ? "2px" : 0,
                            }}
                          >
                            <span style={{
                              fontSize: 14,
                              opacity: column.isSorted ? 1 : 0.5,
                              color: column.isSorted ? "#3b82f6" : "#64748b",
                              transition: "all 0.2s ease",
                              fontWeight: "bold",
                              lineHeight: 1,
                            }}>
                              {column.isSorted ? (column.isSortedDesc ? "▼" : "▲") : "↕"}
                            </span>
                            {column.isSorted && column.sortLabelConfig && (
                              <span style={{
                                fontSize: 9,
                                color: "#3b82f6",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                letterSpacing: "0.02em",
                                lineHeight: 1,
                              }}>
                                {column.isSortedDesc
                                  ? column.sortLabelConfig.desc
                                  : column.sortLabelConfig.asc}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={
                  (headerGroups[0]?.headers?.filter((h) => !isMobile || h.showMobile)
                    .length) || 1
                }
              >
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "200px", padding: "16px", animation: "fadeSlideUp 400ms ease-out" }}>
                  <style>
                    {`@keyframes fadeSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}
                  </style>
                  <div style={{ opacity: 0.95 }}>
                    <svg width="96" height="96" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#9AA3AF" strokeWidth="1.5" />
                      <path d="M22 22L16 16" stroke="#9AA3AF" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="10" cy="10" r="3" stroke="#9AA3AF" strokeWidth="1.5" />
                    </svg>
                  </div>
                  <div style={{ marginTop: "8px", color: "#343a40", fontSize: "20px", fontWeight: 700, textAlign: "center" }}>
                    No matching data
                  </div>
                  <div style={{ marginTop: "4px", color: "#6c757d", fontSize: "15px", textAlign: "center", maxWidth: 560 }}>
                    Refine the filters above to find the results you're looking for.
                  </div>
                </div>
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              prepareRow(row);
              const rowProps = row.getRowProps();
              return (
                <tr
                  {...rowProps}
                  style={{
                    ...rowProps.style,
                    borderBottom: "1px solid #e5e7eb",
                    transition: "background-color 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f8fafc";
                    // Update first column cell background on hover
                    const firstCell = e.currentTarget.querySelector("td:first-child");
                    if (firstCell) {
                      firstCell.style.backgroundColor = "#f8fafc";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    // Reset first column cell background on leave
                    const firstCell = e.currentTarget.querySelector("td:first-child");
                    if (firstCell) {
                      firstCell.style.backgroundColor = "#ffffff";
                    }
                  }}
                >
                  {row.cells
                    .filter((cell) => !isMobile || cell.column.showMobile)
                    .map((cell, cellIndex) => {
                      const cellProps = cell.getCellProps();
                      const isFirstColumn = cellIndex === 0;
                      return (
                        <td
                          className={
                            cell.column.showMobile
                              ? "mobile-table-cell"
                              : "mobile-column"
                          }
                          {...cellProps}
                          style={{
                            ...cellProps.style,
                            position: isFirstColumn ? "sticky" : undefined,
                            left: isFirstColumn ? 0 : undefined,
                            // Keep sticky first column under the sticky header to avoid overlap on Y scroll
                            zIndex: isFirstColumn ? 1 : undefined,
                            backgroundColor: isFirstColumn ? "#ffffff" : undefined,
                            padding: "12px",
                            borderRight: isFirstColumn ? "2px solid #e5e7eb" : "1px solid #f1f5f9",
                            fontSize: "13px",
                            color: "#334155"
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <br />
      {/* <button
        disabled={rowIndex === 0 ? true : false}
        onClick={() => {
          setRowIndex(prevIndex => {
            if (prevIndex > 0) {
              return prevIndex - 1;
            } else {
              return prevIndex;
            }
          });
        }}
        className='btn btn-info'
      >
        Prev
      </button>
      <button
        className='btn btn-info mx-2'
        disabled={
          rowIndex === Math.floor(rows.length / rowsToDisplay) ? true : false
        }
        onClick={() => {
          setRowIndex(prevIndex => {
            if (prevIndex < rows.length - 1) {
              return prevIndex + 1;
            }
          });
        }}
      >
        Next
      </button>

      <span>
        Page {rowIndex + 1} of {Math.floor(rows.length / rowsToDisplay) + 1}
      </span> */}
    </>
  );
}
const ClientsTableView = () => {
  const [dataForTables, setDataForTables] = useState([]);
  const [reportTypeFilter, setReportTypeFilter] = useState("");
  const [reportAddonsFilter, setReportAddonsFilter] = useState("");
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState("live");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [clientTypeFilter, setClientTypeFilter] = useState("");
  const [lisHisNameFilter, setLisHisNameFilter] = useState("");
  const [codebaseTagFilter, setCodebaseTagFilter] = useState("");
  const [clientAlertFilter, setClientAlertFilter] = useState("");
  const [clientAlertMap, setClientAlertMap] = useState({});
  const [alertsLoaded, setAlertsLoaded] = useState(false);
  const [alertFetchInitiated, setAlertFetchInitiated] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [pageLoader, setPageLoader] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [tableKey, setTableKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;
  const tableTopRef = useRef(null);

  const pageClientRows = React.useMemo(() => {
    const rows = Array.isArray(dataForTables) ? dataForTables : [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [dataForTables, currentPage]);

  const [isModalClosed, setIsModalClosed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Invoice Amount State Management
  // invoiceAmounts: Maps composite key (clientId|org|center) to invoice amount
  // Uses composite key to handle duplicate clientIds safely
  const [invoiceAmounts, setInvoiceAmounts] = useState({}); // { [compositeKey]: amount }

  // Per Report Rate State Management
  // perReportRates: Maps composite key (clientId|org|center) to per-report rate
  // Calculated as: (latestInvoiceTotal ÷ 1.18) ÷ reportsSubscribed
  // Uses composite key to handle duplicate clientIds safely
  const [perReportRates, setPerReportRates] = useState({}); // { [compositeKey]: rate }

  // invoiceFetchInitiated: Flag to prevent duplicate API calls on re-renders
  const [invoiceFetchInitiated, setInvoiceFetchInitiated] = useState(false);

  // Activity Count State Management
  // activityCounts: Maps composite key (clientId|org|center) to activity count
  // Uses composite key to handle duplicate clientIds safely
  const [activityCounts, setActivityCounts] = useState({}); // { [compositeKey]: count }

  // activityFetchInitiated: Flag to prevent duplicate API calls on re-renders
  const [activityFetchInitiated, setActivityFetchInitiated] = useState(false);

  // CSV Export State Management
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  const alert = useAlert();

  const handleClose = () => {
    setModalOpen(false);
    setIsModalClosed((prevState) => !prevState);
  };
  const handleShow = () => setModalOpen(true);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    reportTypeFilter,
    reportAddonsFilter,
    subscriptionStatusFilter,
    serviceTypeFilter,
    planTypeFilter,
    clientTypeFilter,
    searchQuery,
  ]);

  const handleRefresh = async () => {
    console.log("Refresh button clicked, current tick:", refreshTick);
    setIsRefreshing(true);

    // Reset fetch flags to allow re-fetching
    setInvoiceFetchInitiated(false);
    setActivityFetchInitiated(false);
    // Preserve cached invoice amounts, activity counts, and feedback ratings
    // They will be loaded from localStorage on next fetch
    // setInvoiceAmounts({}); // Removed - cache preserves this
    // Activity counts and feedback ratings are preserved via localStorage cache

    // Increment refresh tick to trigger all dynamic cells
    setRefreshTick(prev => {
      const newTick = prev + 1;
      console.log("Setting new tick:", newTick);
      return newTick;
    });

    // Force table re-render
    setTableKey(prev => prev + 1);

    // Dispatch a global refresh event
    // Note: Components with cached data (FeedbackRatingCell) will ignore this
    window.dispatchEvent(new CustomEvent('table-refresh', { detail: { timestamp: Date.now() } }));

    // Also trigger a re-fetch of the main data
    try {
      await getAllClients();
      console.log("Main data refreshed");
    } catch (error) {
      console.warn("Error refreshing main data:", error);
    }

    // Reset refreshing state after a delay
    setTimeout(() => {
      console.log("Refresh timeout completed");
      setIsRefreshing(false);
    }, 3000); // Increased timeout to allow for data fetching
  };

  // Simple loading progress based on data availability - DISABLED
  // React.useEffect(() => {
  //   if (dataForTables && dataForTables.length > 0) {
  //     // Simulate loading progress based on data availability
  //     let progress = 0;
  //     const interval = setInterval(() => {
  //       progress += Math.random() * 15; // Random increment between 0-15%
  //       if (progress > 90) progress = 90; // Cap at 90% until data is ready
  //       setLoadingProgress(Math.min(progress, 90));
  //     }, 200);

  //     // Complete loading after a reasonable time
  //     const timeout = setTimeout(() => {
  //       setLoadingProgress(100);
  //       clearInterval(interval);
  //       setTimeout(() => {
  //         setIsInitialLoading(false);
  //       }, 500);
  //     }, 3000); // 3 seconds max loading time

  //     return () => {
  //       clearInterval(interval);
  //       clearTimeout(timeout);
  //     };
  //   }
  // }, [dataForTables]);

  useEffect(() => {
    (async function () {
      try {
        await getAllClients();
        setPageLoader(false);

        // Reset fetch flags when modal closes (refresh triggered)
        if (isModalClosed) {
          setInvoiceFetchInitiated(false);
          setActivityFetchInitiated(false);
        }
      } catch (e) {
        alert.error(e.message);
        setPageLoader(false);
      }
    })();
  }, [isModalClosed]);

  /**
   * useEffect: Fetch invoice amounts when dataForTables is loaded
   * 
   * STATUS-BASED LOADING:
   * - Live clients: Auto-fetch in batches of 10 (fetches for ALL live clients, not just current page)
   * - On Hold / Inactive clients: Skip auto-fetch (show "Load Data" button in cell)
   * 
   * NOTE: Uses dataForTables directly (not pageClientRows) to ensure ALL live clients
   * get their data fetched regardless of which filter or page is active.
   */
  useEffect(() => {
    (async function () {
      // Guard to prevent multiple simultaneous fetches
      if (invoiceFetchInitiated) {
        return;
      }

      // Ensure we have valid data before proceeding
      const allRows = Array.isArray(dataForTables) ? dataForTables : [];
      if (allRows.length === 0) {
        return;
      }

      setInvoiceFetchInitiated(true);

      // Fetch for ALL live clients in the dataset (not just current page)
      // This ensures data is available when user switches filters/pages
      const liveClients = allRows.filter((r) => isLiveSubscriptionRow(r));

      try {
        if (liveClients.length > 0) {
          // Zohobooks customerId is already in the labusers response (client.zohobooks.customerId)
          // No extra API call needed — fetch invoice amounts directly
          await fetchInvoiceAmountsForEligibleClients(liveClients);
        }
      } catch (error) {
        // Reset flag on error to allow retry
        setInvoiceFetchInitiated(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataForTables]);

  /**
   * useEffect: Fetch activity counts when dataForTables is loaded
   * 
   * STATUS-BASED LOADING:
   * - Live clients: Auto-fetch in batches of 10 (fetches for ALL live clients, not just current page)
   * - On Hold / Inactive clients: Skip auto-fetch (show "Load Data" button in cell)
   * 
   * NOTE: Uses dataForTables directly (not pageClientRows) to ensure ALL live clients
   * get their data fetched regardless of which filter or page is active.
   */
  useEffect(() => {
    (async function () {
      // Guard to prevent multiple simultaneous fetches
      if (activityFetchInitiated) {
        return;
      }

      // Ensure we have valid data before proceeding
      const allRows = Array.isArray(dataForTables) ? dataForTables : [];
      if (allRows.length === 0) {
        return;
      }

      setActivityFetchInitiated(true);

      // Fetch for ALL live clients in the dataset (not just current page)
      const liveClients = allRows.filter((r) => isLiveSubscriptionRow(r));

      try {
        if (liveClients.length > 0) {
          // Fetch activity counts for all live clients (batches of 10)
          await fetchActivityCountsForAllClients(liveClients);
        }
      } catch (error) {
        // Reset flag on error to allow retry
        setActivityFetchInitiated(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataForTables]);

  /**
   * useEffect: Background-load alert status for ALL live clients
   *
   * Reuses getLowUsageInfoForClient (cache-first, deduplicates in-flight requests).
   * Processes in batches of 5 to avoid overloading the API.
   * Once complete, sets alertsLoaded=true so the filter dropdown appears.
   */
  useEffect(() => {
    (async function () {
      if (alertFetchInitiated) return;

      const allRows = Array.isArray(dataForTables) ? dataForTables : [];
      if (allRows.length === 0) return;

      setAlertFetchInitiated(true);

      const liveClients = allRows.filter((r) => isLiveSubscriptionRow(r));
      if (liveClients.length === 0) {
        setAlertsLoaded(true);
        return;
      }

      const alertMap = {};
      const batchSize = 5;

      try {
        for (let i = 0; i < liveClients.length; i += batchSize) {
          const batch = liveClients.slice(i, i + batchSize);
          const results = await Promise.allSettled(
            batch.map(async (client) => {
              const key = getClientCompositeKey(client);
              if (!key) return { key: null, info: { isWarning: false } };
              try {
                const info = await getLowUsageInfoForClient(client);
                return { key, info };
              } catch (_) {
                return { key, info: { isWarning: false } };
              }
            })
          );
          results.forEach((result) => {
            if (result.status === "fulfilled" && result.value.key) {
              alertMap[result.value.key] = result.value.info;
            }
          });
        }
      } catch (_) {
        // Silently handle errors — partial data is still useful
      }

      setClientAlertMap(alertMap);
      setAlertsLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataForTables]);

  // Refresh table when subscription is updated from modal
  useEffect(() => {
    const onUpdated = () => {
      try {
        setModalOpen(false);
        setIsModalClosed((prev) => !prev);
        setRefreshTick((t) => t + 1);
        setInvoiceFetchInitiated(false);
        setActivityFetchInitiated(false);
        // Preserve cached invoice amounts, activity counts, and feedback ratings
        // They will be loaded from localStorage on next fetch
        // setInvoiceAmounts({}); // Removed - cache preserves this
        getAllClients();
      } catch (_) { }
    };
    window.addEventListener("subscription-updated", onUpdated);
    return () => window.removeEventListener("subscription-updated", onUpdated);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getAllClients = async () => {
    const api =
      "https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/subscription/labusers";
    const configBody = {
      method: "get",
      url: api,
    };
    const response = await axios(configBody);
    try {
      console.groupCollapsed("API raw • GET labusers");
      console.log("url:", api);
      console.log("status:", response?.status);
      console.log("raw:", response?.data);
      console.log("json:\n" + JSON.stringify(response?.data, null, 2));
      console.groupEnd();
    } catch (_) { }

    setDataForTables(response.data.data);
  };


  /**
   * Resolves userId for a client by matching org and center
   * Used to fetch ZohoBooks customer ID from subscription status API
   * 
   * @param {Object} client - Client object with org and center properties
   * @returns {string} - userId string or empty string if not found
   */
  const getUserIdForClient = (client) => {
    try {
      if (client && client.userId) return client.userId;
      const loginDetailsRaw = localStorage.getItem("loginDetails");
      if (!loginDetailsRaw) return "";
      const clientData = JSON.parse(loginDetailsRaw).clientData || [];
      const selected = clientData.find((each) => each.org === client.org && each.center === client.center);
      return selected ? selected.userId : "";
    } catch (_) {
      return "";
    }
  };

  /**
   * Generates a unique composite key for invoice amounts
   * Uses format: "clientId|org|center" to handle duplicate clientIds safely
   * This ensures each client row has a unique identifier even if clientId is duplicated
   * 
   * @param {Object} client - Client object with clientId, org, and center properties
   * @returns {string|null} - Composite key string or null if client is invalid
   */
  const getInvoiceKey = (client) => {
    if (!client) return null;
    const clientId = client.clientId || "";
    const org = client.org || "";
    const center = client.center || "";
    return `${clientId}|${org}|${center}`;
  };

  // ============================================================================
  // LOCALSTORAGE CACHE HELPER FUNCTIONS
  // ============================================================================
  // 
  // These functions handle loading and saving cached data to localStorage
  // to reduce AWS API costs by avoiding duplicate API calls
  // ============================================================================

  /**
   * Loads activity counts from localStorage cache
   * @returns {Object} - Object with composite keys mapping to activity counts, or empty object
   */
  const loadActivityCountsFromCache = () => {
    try {
      const cached = localStorage.getItem("activityCountsCache");
      if (cached) {
        const parsed = JSON.parse(cached);
        // Return just the data object (without timestamp if present)
        return parsed.data || parsed;
      }
    } catch (error) {
      console.warn("Error loading activity counts from cache:", error);
    }
    return {};
  };

  /**
   * Saves activity counts to localStorage cache
   * @param {Object} data - Object with composite keys mapping to activity counts
   */
  const saveActivityCountsToCache = (data) => {
    try {
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem("activityCountsCache", JSON.stringify(cacheData));
    } catch (error) {
      console.warn("Error saving activity counts to cache:", error);
    }
  };

  /**
   * Loads feedback ratings from localStorage cache
   * @returns {Object} - Object with composite keys mapping to feedback ratings, or empty object
   */
  const loadFeedbackRatingsFromCache = () => {
    try {
      const cached = localStorage.getItem("feedbackRatingsCache");
      if (cached) {
        const parsed = JSON.parse(cached);
        // Return just the data object (without timestamp if present)
        return parsed.data || parsed;
      }
    } catch (error) {
      console.warn("Error loading feedback ratings from cache:", error);
    }
    return {};
  };

  /**
   * Saves feedback ratings to localStorage cache
   * @param {Object} data - Object with composite keys mapping to feedback ratings
   */
  const saveFeedbackRatingsToCache = (data) => {
    try {
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem("feedbackRatingsCache", JSON.stringify(cacheData));
    } catch (error) {
      console.warn("Error saving feedback ratings to cache:", error);
    }
  };

  /**
   * Loads invoice amounts and per-report rates from localStorage cache
   * @returns {Object} - Object with keys: { invoiceAmounts: {...}, perReportRates: {...} }
   */
  const loadInvoiceAmountsFromCache = () => {
    try {
      const cached = localStorage.getItem("invoiceAmountsCache");
      if (cached) {
        const parsed = JSON.parse(cached);
        const data = parsed.data || parsed;
        // Handle both new format (with invoiceAmounts/perReportRates) and legacy format (plain object)
        if (data.invoiceAmounts || data.perReportRates) {
          return data;
        }
        // Legacy: plain object is invoiceAmounts only
        return { invoiceAmounts: data, perReportRates: {} };
      }
    } catch (error) {
      console.warn("Error loading invoice amounts from cache:", error);
    }
    return { invoiceAmounts: {}, perReportRates: {} };
  };

  /**
   * Saves invoice amounts and per-report rates to localStorage cache
   * @param {Object} invoiceMap - Object with composite keys mapping to invoice amounts
   * @param {Object} rateMap - Object with composite keys mapping to per-report rates
   */
  const saveInvoiceAmountsToCache = (invoiceMap, rateMap) => {
    try {
      const cacheData = {
        data: {
          invoiceAmounts: invoiceMap,
          perReportRates: rateMap || {}
        },
        timestamp: Date.now()
      };
      localStorage.setItem("invoiceAmountsCache", JSON.stringify(cacheData));
    } catch (error) {
      console.warn("Error saving invoice amounts to cache:", error);
    }
  };

  /**
   * Reads the Zohobooks customerId directly from the client object.
   * The labusers API already returns zohobooks.customerId — no extra API call needed.
   *
   * @param {Object} client - Client object from labusers response
   * @returns {string|null} - customerId string or null if not present
   */
  const getZohobooksCustomerId = (client) => {
    return client?.zohobooks?.customerId || null;
  };

  /**
   * Formats date string from "YYYY-MM-DD HH:mm:ss.SSS" to "YYYY-MM-DD"
   * Required for invoice API which expects date in YYYY-MM-DD format
   * 
   * @param {string} dateString - Date string in format "YYYY-MM-DD HH:mm:ss.SSS"
   * @returns {string} - Formatted date string "YYYY-MM-DD" or empty string if invalid
   */
  const formatDateForInvoice = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "";
    return dateString.split(" ")[0];
  };

  /**
   * Fetches invoice amounts for eligible clients in batches.
   *
   * Eligibility criteria (all read directly from the labusers response — no extra API needed):
   * - Must have startDate and endDate (not empty/null/undefined)
   * - Must have clientId
   * - Must have zohobooks.customerId (already present in labusers response)
   *
   * Process:
   * 1. Initialize all clients with "Loading..." state
   * 2. Filter eligible clients based on criteria above
   * 3. Fetch invoice amounts in chunks of 10 concurrent requests
   * 4. Sum totals from all invoices in the response data array
   * 5. Update state with final amounts or "Not available"
   *
   * Uses composite keys (clientId|org|center) to handle duplicate clientIds safely
   */
  const fetchInvoiceAmountsForEligibleClients = async (clientsList) => {
    try {
      const sourceRows = Array.isArray(clientsList) ? clientsList : dataForTables;
      if (!sourceRows || sourceRows.length === 0) {
        return;
      }

      // Get authorization token for invoice API
      const idToken = localStorage.getItem("idTokenBilling");

      // Step 1: Load cached data from localStorage first
      const cached = loadInvoiceAmountsFromCache();
      const invoiceMap = { ...cached.invoiceAmounts };
      const rateMap = { ...cached.perReportRates };

      // Step 2: Initialize invoiceMap and rateMap with cached data or "Loading..." for all clients
      sourceRows.forEach((client) => {
        const key = getInvoiceKey(client);
        if (key && !invoiceMap[key]) {
          // Only set "Loading..." if not in cache
          invoiceMap[key] = "Loading...";
        }
        if (key && !rateMap[key]) {
          // Only set "Loading..." if not in cache
          rateMap[key] = "Loading...";
        }
      });
      setInvoiceAmounts({ ...invoiceMap });
      setPerReportRates({ ...rateMap });

      // Step 3: Filter eligible clients and mark non-eligible clients as "Not available"
      // Eligibility is determined entirely from data already in the labusers response
      // No extra API call needed for Zohobooks ID — it's in client.zohobooks.customerId
      const eligibleClients = [];
      for (const client of sourceRows) {
        const startDate = client.startDate;
        const endDate = client.endDate;
        const clientId = client.clientId;
        // Read customerId directly from labusers response object
        const zohobooksCustomerId = getZohobooksCustomerId(client);
        const key = getInvoiceKey(client);

        // Client is eligible if it has all required values
        const hasValidDates = startDate &&
          startDate !== "" &&
          startDate !== "null" &&
          startDate !== "undefined" &&
          endDate &&
          endDate !== "" &&
          endDate !== "null" &&
          endDate !== "undefined";

        const hasZohobooksId = zohobooksCustomerId && zohobooksCustomerId !== "";
        const hasClientId = clientId && clientId !== "";

        if (!hasValidDates || !hasZohobooksId || !hasClientId) {
          // Mark non-eligible clients as "Not available" immediately
          if (key && !cached.invoiceAmounts[key]) {
            invoiceMap[key] = "Not available";
          }
          if (key && !cached.perReportRates[key]) {
            rateMap[key] = "Not available";
          }
        } else if (key && !cached.invoiceAmounts[key]) {
          // Only add to eligibleClients if not in cache
          eligibleClients.push({
            clientId,
            startDate: formatDateForInvoice(startDate),
            endDate: formatDateForInvoice(endDate),
            org: client.org,
            center: client.center,
            customerName: client.customerName || client.displayName || "",
            reportsSubscribed: client.reportsSubscribed, // Include for per-report rate calculation
            invoiceKey: key, // Composite key for unique identification
          });
        }
      }

      // Update state with non-eligible clients marked as "Not available"
      setInvoiceAmounts({ ...invoiceMap });
      setPerReportRates({ ...rateMap });

      // Step 4: Process eligible clients (not in cache) in chunks of 10 concurrent requests
      if (eligibleClients.length === 0) {
        // All data is cached, save to ensure timestamp is updated
        saveInvoiceAmountsToCache(invoiceMap, rateMap);
        return;
      }

      const chunkSize = 10;

      for (let i = 0; i < eligibleClients.length; i += chunkSize) {
        const chunk = eligibleClients.slice(i, i + chunkSize);

        // Process chunk concurrently
        const promises = chunk.map(async (client) => {
          try {
            const url = `https://api.niroggyan.com/billing/invoice?clientId=${client.clientId}&startDate=${client.startDate}&endDate=${client.endDate}`;
            const res = await axios({
              method: "GET",
              url,
              headers: {
                authorization: idToken,
                "Content-Type": "application/json"
              },
              timeout: 10000,
            });

            // Extract invoice amount and currency info from response
            // Response structure: { isSuccess: true, data: [{ total: 112602, date: "2025-06-01", currency_code: "INR", currency_symbol: "₹", ... }] }
            let amount = null;
            let currencyCode = null;
            let currencySymbol = null;
            let perReportRate = null;

            if (res?.data?.isSuccess && Array.isArray(res?.data?.data)) {
              if (res.data.data.length === 0) {
                // Empty data array means no invoices found
                amount = null;
              } else {
                // Find latest invoice by date
                let latestInvoice = res.data.data[0];
                for (let i = 1; i < res.data.data.length; i++) {
                  const currentInvoiceDate = new Date(res.data.data[i]?.date || 0);
                  const latestInvoiceDate = new Date(latestInvoice?.date || 0);
                  if (currentInvoiceDate > latestInvoiceDate) {
                    latestInvoice = res.data.data[i];
                  }
                }

                // Use latest invoice's total for Invoice Amount column
                const latestTotal = latestInvoice?.total || 0;
                amount = typeof latestTotal === 'number' ? latestTotal : parseFloat(latestTotal) || 0;

                // Take currency info from the latest invoice
                currencyCode = latestInvoice?.currency_code || null;
                currencySymbol = latestInvoice?.currency_symbol || null;

                // Calculate per-report rate: (latestTotal ÷ 1.18) ÷ reportsSubscribed
                // Only if reportsSubscribed is available and > 0
                const reportsSubscribed = client.reportsSubscribed || 0;
                if (latestInvoice && reportsSubscribed > 0) {
                  const gstRemovedAmount = currencyCode === 'INR' ? latestTotal / 1.18 : latestTotal;
                  perReportRate = {
                    amount: gstRemovedAmount / reportsSubscribed,
                    currencyCode,
                    currencySymbol
                  };
                }
              }
            }

            // Store as an object so the display cell has full currency context
            const finalPayload = amount !== null && amount !== undefined
              ? { amount, currencyCode, currencySymbol }
              : "Not available";

            const finalRatePayload = perReportRate !== null && perReportRate !== undefined
              ? perReportRate
              : "Not available";

            return {
              invoiceKey: client.invoiceKey, // Composite key for unique identification
              amount: finalPayload,
              rate: finalRatePayload,
            };
          } catch (error) {
            // On error, mark as "Not available"
            return {
              invoiceKey: client.invoiceKey,
              amount: "Not available",
              rate: "Not available",
            };
          }
        });

        // Wait for all requests in chunk to complete
        const results = await Promise.all(promises);

        // Update invoiceMap and rateMap with results from this chunk
        results.forEach((result) => {
          if (result.invoiceKey) {
            invoiceMap[result.invoiceKey] = result.amount;
            rateMap[result.invoiceKey] = result.rate;
          }
        });

        // Update state immediately after each chunk completes
        // This displays data incrementally as each batch of 10 APIs completes
        // Merge with existing state to preserve "Loading..." for unprocessed clients
        setInvoiceAmounts((prevState) => {
          const updatedState = { ...prevState };
          results.forEach((result) => {
            if (result.invoiceKey) {
              updatedState[result.invoiceKey] = result.amount;
            }
          });
          return updatedState;
        });

        setPerReportRates((prevState) => {
          const updatedState = { ...prevState };
          results.forEach((result) => {
            if (result.invoiceKey) {
              updatedState[result.invoiceKey] = result.rate;
            }
          });
          return updatedState;
        });
      }

      // Step 5: Save all fetched data (cached + newly fetched) to localStorage
      saveInvoiceAmountsToCache(invoiceMap, rateMap);

    } catch (error) {
      // On complete failure, mark all clients as "Not available"
      const errorMap = {};
      const errorRateMap = {};
      const sourceRows = Array.isArray(clientsList) ? clientsList : dataForTables;
      if (sourceRows && sourceRows.length > 0) {
        sourceRows.forEach((client) => {
          const key = getInvoiceKey(client);
          if (key) {
            errorMap[key] = "Not available";
            errorRateMap[key] = "Not available";
          }
        });
      }
      setInvoiceAmounts(errorMap);
      setPerReportRates(errorRateMap);
    }
  };

  /**
   * Fetches activity counts for all clients in batches
   * 
   * Eligibility criteria:
   * - Must have clientId
   * 
   * Process:
   * 1. Initialize all clients with "Loading..." state
   * 2. Filter eligible clients based on criteria
   * 3. Fetch activity counts in chunks of 10 concurrent requests
   * 4. Update state incrementally after each chunk completes
   * 
   * Uses composite keys (clientId|org|center) to handle duplicate clientIds safely
   */
  const fetchActivityCountsForAllClients = async (clientsList) => {
    try {
      const sourceRows = Array.isArray(clientsList) ? clientsList : dataForTables;
      if (!sourceRows || sourceRows.length === 0) {
        return;
      }

      // Step 1: Always start fresh — activity counts change in real time, skip cache
      // Other caches (invoice, feedback, low-usage) are NOT affected by this change
      const cachedData = {};
      const activityMap = {};

      // Step 2: Initialize activityMap with cached data or "Loading..." for all clients
      sourceRows.forEach((client) => {
        const key = getInvoiceKey(client);
        if (key && !activityMap[key]) {
          // Only set "Loading..." if not in cache
          activityMap[key] = "Loading...";
        }
      });
      setActivityCounts({ ...activityMap });

      // Step 3: Filter eligible clients (must have clientId) that need fetching
      const eligibleClients = [];
      for (const client of sourceRows) {
        const clientId = client.clientId;
        const hasClientId = clientId && clientId !== "";
        const key = getInvoiceKey(client);

        if (!hasClientId) {
          // Mark non-eligible clients as "0" immediately
          if (key) {
            activityMap[key] = "0";
          }
        } else if (key && !cachedData[key]) {
          // Only add to eligibleClients if not in cache
          eligibleClients.push({
            clientId,
            org: client.org,
            center: client.center,
            customerName: client.customerName || client.displayName || "",
            activityKey: key, // Composite key for unique identification
          });
        }
      }

      // Update state with non-eligible clients marked as "0"
      setActivityCounts({ ...activityMap });

      // Step 4: Process eligible clients (not in cache) in chunks of 10 concurrent requests
      if (eligibleClients.length === 0) {
        // All data is cached, save to ensure timestamp is updated
        saveActivityCountsToCache(activityMap);
        return;
      }

      const chunkSize = 10;

      for (let i = 0; i < eligibleClients.length; i += chunkSize) {
        const chunk = eligibleClients.slice(i, i + chunkSize);

        // Process chunk concurrently
        const promises = chunk.map(async (client) => {
          // getActivityCount always returns a number (0 on error/failure)
          const count = await getActivityCount(client);

          // Console log activity count with client name for debugging
          const clientName = client.customerName || client.org || "Unknown";
          console.log(`[Activity Count] ${clientName}: ${count !== null && count !== undefined ? count : 0}`);

          return {
            activityKey: client.activityKey,
            count: count !== null && count !== undefined ? String(count) : "0",
          };
        });

        // Wait for all requests in chunk to complete
        const results = await Promise.all(promises);

        // Update activityMap with results from this chunk
        results.forEach((result) => {
          if (result.activityKey) {
            activityMap[result.activityKey] = result.count;
          }
        });

        // Update state immediately after each chunk completes
        setActivityCounts((prevState) => {
          const updatedState = { ...prevState };
          results.forEach((result) => {
            if (result.activityKey) {
              updatedState[result.activityKey] = result.count;
            }
          });
          return updatedState;
        });
      }

      // Step 5: Save all fetched data (cached + newly fetched) to localStorage
      saveActivityCountsToCache(activityMap);

    } catch (error) {
      // On complete failure, mark all clients as "0"
      const errorMap = {};
      const sourceRows = Array.isArray(clientsList) ? clientsList : dataForTables;
      if (sourceRows && sourceRows.length > 0) {
        sourceRows.forEach((client) => {
          const key = getInvoiceKey(client);
          if (key) {
            errorMap[key] = "0";
          }
        });
      }
      setActivityCounts(errorMap);
    }
  };


  // ============================================================================
  // MANUAL LOAD FUNCTIONS (for "Load Data" buttons on non-live clients)
  // ============================================================================

  /**
   * Manually fetch invoice amount for a single non-live client (on-hold / inactive)
   * Called when user clicks "Load Data" button in Invoice Amount column
   */
  const loadInvoiceForSingleClient = async (client) => {
    const key = getInvoiceKey(client);
    if (!key) return;

    // Set loading state for both invoice amounts and per-report rates
    setInvoiceAmounts((prev) => ({ ...prev, [key]: "Loading..." }));
    setPerReportRates((prev) => ({ ...prev, [key]: "Loading..." }));

    try {
      // Zohobooks customerId is already in client.zohobooks.customerId (from labusers response)
      // No extra API call needed — fetch invoice amount directly
      await fetchInvoiceAmountsForEligibleClients([client]);
    } catch (error) {
      setInvoiceAmounts((prev) => ({ ...prev, [key]: "Not available" }));
      setPerReportRates((prev) => ({ ...prev, [key]: "Not available" }));
    }
  };

  /**
   * Manually fetch activity count for a single non-live client (on-hold / inactive)
   * Called when user clicks "Load Data" button in Activity Count column
   */
  const loadActivityForSingleClient = async (client) => {
    const key = getInvoiceKey(client);
    if (!key) return;

    // Set loading state
    setActivityCounts((prev) => ({ ...prev, [key]: "Loading..." }));

    try {
      await fetchActivityCountsForAllClients([client]);
    } catch (error) {
      setActivityCounts((prev) => ({ ...prev, [key]: "0" }));
    }
  };

  // Hide specific demo/test clients from the table and top counts (without deleting)
  const HIDDEN_CLIENT_TOKENS = React.useMemo(() => {
    return new Set([
      "demo_user",
      "dummy user",
      "trial_center",
      "trial_center1",
      "testing user",
      "testing client",
      "demo",
      "trial_center5",
      "ng",
      "ohc",
      "b2c niro",
      "testingclient3",
      "happiesthealth_uat",
      "fortis",
      "fortis hospital"
    ]);
  }, []);

  const shouldHideClient = React.useCallback(
    (row) => {
      if (!row || typeof row !== "object") return false;
      const name = (row.displayName || "").toString().trim().toLowerCase();
      const customerName = (row.customerName || "").toString().trim().toLowerCase();
      const org = (row.org || "").toString().trim().toLowerCase();
      const center = (row.center || "").toString().trim().toLowerCase();
      // Hide when display name, customer name, org, or center matches the hidden tokens
      return (
        HIDDEN_CLIENT_TOKENS.has(name) ||
        HIDDEN_CLIENT_TOKENS.has(customerName) ||
        HIDDEN_CLIENT_TOKENS.has(org) ||
        HIDDEN_CLIENT_TOKENS.has(center)
      );
    },
    [HIDDEN_CLIENT_TOKENS]
  );

  // All rows after applying the hidden-clients filter
  const visibleAllRows = React.useMemo(() => {
    const rows = Array.isArray(dataForTables) ? dataForTables : [];
    return rows.filter((r) => !shouldHideClient(r));
  }, [dataForTables, shouldHideClient]);

  // Dynamic add-ons list: union of MASTER_ADDONS + all add-ons seen across live client data.
  // This means new add-ons added to any client automatically appear in the filter dropdown.
  const dynamicAddonOptions = React.useMemo(() => {
    const fromApi = new Set();
    (visibleAllRows || []).forEach((row) => {
      if (Array.isArray(row.reportAddons)) {
        row.reportAddons.forEach((addon) => {
          if (addon && typeof addon === "string" && addon.trim()) {
            fromApi.add(addon.trim());
          }
        });
      }
    });
    // Merge master list first (preserves master order), then add any API-only extras
    const merged = new Set([...MASTER_ADDONS, ...fromApi]);
    return Array.from(merged);
  }, [visibleAllRows]);

  // Dynamic LIS/HIS Names list: unique names across all live clients
  const dynamicLisHisNameOptions = React.useMemo(() => {
    const fromApiMap = new Map();
    (visibleAllRows || []).forEach((row) => {
      const val = row.lisHisName;
      if (val && typeof val === "string" && val.trim()) {
        const trimmed = val.trim();
        const lower = trimmed.toLowerCase();
        if (!fromApiMap.has(lower)) {
          fromApiMap.set(lower, trimmed);
        }
      }
    });
    return Array.from(fromApiMap.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [visibleAllRows]);

  // Dynamic Codebase Tag list: unique names across all live clients
  const dynamicCodebaseTagOptions = React.useMemo(() => {
    const fromApiMap = new Map();
    (visibleAllRows || []).forEach((row) => {
      const val = row.codebaseTag;
      if (val && typeof val === "string" && val.trim()) {
        const trimmed = val.trim();
        const lower = trimmed.toLowerCase();
        if (!fromApiMap.has(lower)) {
          fromApiMap.set(lower, trimmed);
        }
      }
    });
    return Array.from(fromApiMap.values()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [visibleAllRows]);

  // Function to automatically load counts for all clients
  const loadAllClientCounts = async (clientsData) => {
    if (!clientsData || !Array.isArray(clientsData)) return;

    const idToken = localStorage.getItem("idTokenBilling");
    const clientData = JSON.parse(localStorage.getItem("loginDetails"))?.clientData;

    if (!idToken || !clientData) return;

    // Set all clients to loading state initially
    setDataForTables((existingData) => {
      return existingData.map((eachData) => ({
        ...eachData,
        loader: true
      }));
    });

    // Process clients in batches to avoid overwhelming the API
    const batchSize = 5;
    for (let i = 0; i < clientsData.length; i += batchSize) {
      const batch = clientsData.slice(i, i + batchSize);

      // Process batch concurrently
      const promises = batch.map(async (client) => {
        try {
          const selectedClientDetails = clientData.find(
            (each) => each.org === client.org && each.center === client.center
          );

          if (!selectedClientDetails) return;

          const userId = selectedClientDetails.userId;
          const configBody = {
            headers: {
              authorization: idToken,
            },
            method: "get",
            url: `https://api.niroggyan.com/billing/smartreport/counts?query_on=counts&query_by=subscription&userId=${userId}&cycle=`,
            validateStatus: function (status) {
              return (status >= 200) && (status < 510);
            },
          };

          const response = await axios(configBody);

          if (response.data.isSuccess) {
            return {
              org: client.org,
              center: client.center,
              count: response.data.data[0].count,
              loader: false
            };
          } else {
            return {
              org: client.org,
              center: client.center,
              count: null,
              loader: false
            };
          }
        } catch (e) {
          console.log(`Error fetching count for ${client.org}/${client.center}:`, e);
          return {
            org: client.org,
            center: client.center,
            count: null,
            loader: false
          };
        }
      });

      // Wait for batch to complete
      const results = await Promise.all(promises);

      // Update data with results
      setDataForTables((existingData) => {
        return existingData.map((eachData) => {
          const result = results.find(
            (r) => r.org === eachData.org && r.center === eachData.center
          );
          if (result) {
            return {
              ...eachData,
              count: result.count,
              loader: result.loader
            };
          }
          return eachData;
        });
      });

      // Small delay between batches to be gentle on the API
      if (i + batchSize < clientsData.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  };

  // Helper: normalize report type from row to display label
  const deriveReportType = (row) => {
    if (!row || typeof row !== "object") return "";
    // reportType is not available in the current API response
    // Return empty string for now
    return "";
  };

  // Helper: normalize service type from row to display label (API/Portal)
  const deriveServiceType = (row) => {
    if (!row || typeof row !== "object") return "";
    // Use serviceType directly from API
    const explicit = (row.serviceType || "").toString().trim();
    if (explicit) {
      const normalized = explicit.toLowerCase();
      return normalized === "portal" ? "Portal" : normalized === "api" ? "API" : explicit;
    }
    return "";
  };

  const deriveNumberOfTickets = (row) => {
    if (!row || typeof row !== "object") return "";
    // Use numberOfTickets directly from API
    const v = row.numberOfTickets;
    if (v === undefined || v === null || v === "") return "";
    // Handle both string and number types
    const n = Number(v);
    return Number.isFinite(n) ? String(n) : "";
  };

  // Helper: derive Plan Type from row
  const derivePlanType = (row) => {
    if (!row || typeof row !== "object") return "";
    // Use planType directly from API
    const v = (row.planType || "").toString().trim();
    return v;
  };


  // Helper: derive NPS score from row (as string) for display and CSV
  const deriveNpsScore = (row) => {
    if (!row || typeof row !== "object") return "";
    // Use npsScore directly from API
    const v = row.npsScore;
    if (v === undefined || v === null || v === "") return "";
    const n = Number(v);
    return Number.isFinite(n) ? String(n) : "";
  };

  // Helper: derive displayed Report Add-ons list from row
  const deriveReportAddons = (row) => {
    if (!row) return [];
    // Use reportAddons directly from API
    if (Array.isArray(row.reportAddons) && row.reportAddons.length) return row.reportAddons;
    return [];
  };

  // Helper: derive Client Type from row
  const deriveClientType = (row) => {
    if (!row || typeof row !== "object") return "";
    // Use clientType directly from API
    const v = (row.clientType || "").toString().trim();
    return v;
  };

  // Filtered data based on Report Type selection
  const filteredDataForTables = React.useMemo(() => {
    let rows = visibleAllRows || [];
    if (reportTypeFilter) {
      const target = reportTypeFilter.toString().trim().toLowerCase();
      rows = rows.filter((r) => {
        const val = (r.reportType || deriveReportType(r) || "").toString().trim().toLowerCase();
        if (target === "not available") return val === "" || val === "not available";
        return val === target;
      });
    }
    if (reportAddonsFilter) {
      const targetAddon = reportAddonsFilter.toString().trim().toLowerCase();
      rows = rows.filter((r) => {
        const addons = (deriveReportAddons(r) || []).map((s) => (s || "").toString().trim().toLowerCase());
        if (targetAddon === "no add-ons") {
          return addons.length === 0 || (addons.length === 1 && addons[0] === "");
        }
        return addons.includes(targetAddon);
      });
    }
    if (subscriptionStatusFilter) {
      const target = subscriptionStatusFilter.trim().toLowerCase();
      rows = rows.filter((r) => {
        const status = (r.subscriptionStatus || "").toString().trim().toLowerCase();
        // Handle "on hold" / "onhold" variations
        if (target === "on hold" || target === "onhold") {
          return status === "on hold" || status === "onhold" || status === "on-hold" || status === "on_hold" || status === "hold";
        }
        return status === target;
      });
    }
    if (serviceTypeFilter) {
      const target = serviceTypeFilter.toString().trim().toLowerCase();
      rows = rows.filter((r) => {
        const val = (deriveServiceType(r) || "").toString().trim().toLowerCase();
        if (target === "not available") return val === "" || val === "not available";
        return val === target;
      });
    }
    if (planTypeFilter) {
      const target = planTypeFilter.toString().trim().toLowerCase();
      rows = rows.filter((r) => {
        const val = (derivePlanType(r) || "").toString().trim().toLowerCase();
        if (target === "not available") return val === "" || val === "not available";
        return val === target;
      });
    }
    if (clientTypeFilter) {
      const target = clientTypeFilter.toString().trim().toLowerCase();
      rows = rows.filter((r) => {
        const val = (deriveClientType(r) || "").toString().trim().toLowerCase();
        if (target === "not available") return val === "" || val === "not available";
        return val === target;
      });
    }
    if (lisHisNameFilter) {
      const target = lisHisNameFilter.toString().trim().toLowerCase();
      rows = rows.filter((r) => {
        const val = (r.lisHisName || "").toString().trim().toLowerCase();
        if (target === "not available") return val === "" || val === "not available";
        return val === target;
      });
    }
    if (codebaseTagFilter) {
      const target = codebaseTagFilter.toString().trim().toLowerCase();
      rows = rows.filter((r) => {
        const val = (r.codebaseTag || "").toString().trim().toLowerCase();
        if (target === "not available") return val === "" || val === "not available";
        return val === target;
      });
    }
    if (clientAlertFilter && alertsLoaded) {
      rows = rows.filter((r) => {
        const key = getClientCompositeKey(r);
        const info = key ? clientAlertMap[key] : null;
        if (!info) return false;
        if (clientAlertFilter === "low_usage") {
          return !!(info.isLowUsage);
        }
        if (clientAlertFilter === "critical") {
          return !!(info.isCritical);
        }
        return true;
      });
    }
    // Apply search filter: match against client name, display name, clientId, org, center
    if (searchQuery) {
      const searchLower = searchQuery.trim().toLowerCase();
      if (searchLower === "") return rows;
      rows = rows.filter((r) => {
        const customerName = (r.customerName || "").toString().trim().toLowerCase();
        const displayName = (r.displayName || "").toString().trim().toLowerCase();
        const clientId = (r.clientId || "").toString().trim().toLowerCase();
        const org = (r.org || "").toString().trim().toLowerCase();
        const center = (r.center || "").toString().trim().toLowerCase();
        return (
          customerName.includes(searchLower) ||
          displayName.includes(searchLower) ||
          clientId.includes(searchLower) ||
          org.includes(searchLower) ||
          center.includes(searchLower)
        );
      });
    }
    return rows;
  }, [visibleAllRows, reportTypeFilter, reportAddonsFilter, subscriptionStatusFilter, serviceTypeFilter, planTypeFilter, clientTypeFilter, lisHisNameFilter, codebaseTagFilter, clientAlertFilter, alertsLoaded, clientAlertMap, searchQuery]);

  const totalRows = filteredDataForTables ? filteredDataForTables.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedDataForTables = React.useMemo(() => {
    const rows = filteredDataForTables || [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [filteredDataForTables, currentPage]);

  const handlePageChange = (nextPage) => {
    const next = Math.min(Math.max(1, nextPage), totalPages);
    setCurrentPage(next);
    setTableKey((prev) => prev + 1);
    try {
      tableTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (_) { }
  };

  const getPaginationModel = () => {
    const items = [];
    if (totalPages <= 1) return items;

    const maxNumeric = 5;
    let start = Math.max(1, currentPage - Math.floor(maxNumeric / 2));
    let end = start + maxNumeric - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxNumeric + 1);
    }

    if (start > 1) {
      items.push({ type: "page", page: 1, key: "page-1" });
      if (start > 2) items.push({ type: "ellipsis", key: "start-ellipsis" });
    }

    for (let p = start; p <= end; p += 1) {
      if (p === 1 || p === totalPages) continue;
      items.push({ type: "page", page: p, key: `page-${p}` });
    }

    if (end < totalPages) {
      if (end < totalPages - 1) items.push({ type: "ellipsis", key: "end-ellipsis" });
      items.push({ type: "page", page: totalPages, key: `page-${totalPages}` });
    }

    return items;
  };







  // util: format date as dd-mm-yyyy from `yyyy-mm-dd HH:mm:ss.xxx` or `yyyy-mm-dd`
  const formatDateForExport = (isoLike) => {
    if (!isoLike || typeof isoLike !== "string") return "";
    const raw = isoLike.split(" ")[0];
    const parts = raw.split("-");
    if (parts.length !== 3) return isoLike;
    const [y, m, d] = parts;
    const yy = (y || "").slice(0);
    return `${d}-${m}-${yy}`;
  };

  /**
   * Fetch all missing data for a single row for CSV export
   * Returns enriched row with all fields populated
   */
  const fetchDataForSingleRow = async (row) => {
    const result = { ...row };
    const idToken = localStorage.getItem("idTokenBilling");

    try {
      // Get userId for this row
      const getUserId = () => {
        if (row && row.userId) return row.userId;
        try {
          const loginDetailsRaw = localStorage.getItem("loginDetails");
          if (!loginDetailsRaw) return "";
          const clientData = JSON.parse(loginDetailsRaw).clientData || [];
          const selected = clientData.find((each) => each.org === row.org && each.center === row.center);
          return selected ? selected.userId : "";
        } catch {
          return "";
        }
      };

      const userId = getUserId();

      // Fetch customer name, report type, and addons if missing
      if (!result.customerName || !result.reportType || !result.reportAddons || (Array.isArray(result.reportAddons) && result.reportAddons.length === 0)) {
        try {
          const reportTypeData = await getClientReportType({ org: row.org, center: row.center });
          if (reportTypeData && typeof reportTypeData === 'object') {
            if (!result.customerName && reportTypeData.customerName) {
              result.customerName = reportTypeData.customerName;
            }
            if (!result.reportType && reportTypeData.reportType) {
              result.reportType = reportTypeData.reportType;
            }
            if ((!result.reportAddons || (Array.isArray(result.reportAddons) && result.reportAddons.length === 0)) && reportTypeData.reportAddons) {
              result.reportAddons = reportTypeData.reportAddons;
            }
          }
        } catch (error) {
          // If fetch fails, keep existing data or empty
        }
      }

      // Fetch reports consumed (current) if missing
      if (result.count === undefined || result.count === null || result.count === "") {
        try {
          const count = await getClientReportsCount({ org: row.org, center: row.center }, "reports", false);
          result.count = count ?? "";
        } catch (error) {
          result.count = "";
        }
      }

      // Fetch reports consumed (previous) if missing
      if (result.previousCount === undefined || result.previousCount === null || result.previousCount === "") {
        try {
          const previousCount = await getClientReportsCount({ org: row.org, center: row.center }, "reports", true);
          result.previousCount = previousCount ?? "";
        } catch (error) {
          result.previousCount = "";
        }
      }

      // Always fetch feedback rating (currently broken in original export)
      try {
        const feedback = await getClientFeedback({ org: row.org, center: row.center });
        result.feedbackRating = feedback || "Not Available";
      } catch (error) {
        result.feedbackRating = "Not Available";
      }

      // Always fetch activity count (new field for export)
      try {
        const activityCount = await getActivityCount({ clientId: row.clientId, org: row.org, center: row.center });
        result.activityCount = activityCount !== null && activityCount !== undefined ? String(activityCount) : "0";
      } catch (error) {
        result.activityCount = "0";
      }

      // Always fetch invoice amount (new field for export)
      if (userId && row.startDate && row.endDate && row.clientId && idToken) {
        try {
          const url = `https://api.niroggyan.com/billing/invoice?clientId=${row.clientId}&startDate=${row.startDate}&endDate=${row.endDate}`;
          const res = await axios({
            method: "GET",
            url,
            headers: {
              authorization: idToken,
              "Content-Type": "application/json"
            },
            timeout: 10000,
          });

          let amount = null;
          if (res?.data?.isSuccess && Array.isArray(res?.data?.data)) {
            if (res.data.data.length === 0) {
              amount = null;
            } else {
              // Sum all totals from the data array
              amount = res.data.data.reduce((sum, invoice) => {
                const invoiceTotal = invoice?.total || 0;
                return sum + (typeof invoiceTotal === 'number' ? invoiceTotal : parseFloat(invoiceTotal) || 0);
              }, 0);
            }
          }

          result.invoiceAmount = amount !== null && amount !== undefined ? String(amount) : "Not Available";
        } catch (error) {
          result.invoiceAmount = "Not Available";
        }
      } else {
        result.invoiceAmount = "Not Available";
      }

    } catch (error) {
      console.error("Error fetching data for row:", error);
    }

    return result;
  };

  /**
   * Fetch all missing data for multiple rows with batch processing and progress tracking
   * Processes rows in batches to avoid overwhelming the server
   */
  const fetchAllDataForExport = async (rows) => {
    if (!rows || rows.length === 0) return [];

    const BATCH_SIZE = 10; // Process 10 rows concurrently
    const enrichedRows = [];

    // Initialize progress
    setExportProgress({ current: 0, total: rows.length });

    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      const batch = rows.slice(i, i + BATCH_SIZE);

      // Process batch concurrently
      const batchResults = await Promise.all(
        batch.map(row => fetchDataForSingleRow(row))
      );

      enrichedRows.push(...batchResults);

      // Update progress after each batch
      setExportProgress({
        current: Math.min(i + BATCH_SIZE, rows.length),
        total: rows.length
      });
    }

    return enrichedRows;
  };

  // Build and download CSV that opens in Excel, fetching missing fields needed for export
  const exportTableToCSV = async () => {
    try {
      // Set loading state
      setIsExporting(true);
      setExportProgress({ current: 0, total: 0 });

      // Get ALL filtered rows (not paginated - this exports all clients matching current filters)
      const rows = filteredDataForTables || [];

      if (rows.length === 0) {
        alert.error("No data to export");
        return;
      }

      // Updated headers - removed "Reports consumed (previous month)" and "Reports consumed (previous)", added "Invoice Amount" and "Activity Count"
      const headers = [
        "Client Name",
        "Mail ID",
        "Reports Subscribed",
        "Reports consumed (current)",
        "Average Feedback",
        "Invoice Amount (Current Subscription)",
        "Activity Count",
        "Live Date",
        "Subscription Start Date",
        "Subscription End Date",
        "Subscription Status",
        "LIS/HIS Name",
        "Service Type",
        "Report Type",
        "Report Add-ons",
        "Number of Tickets",
        "NPS Score",
        "Plan Type",
        "Client Type",
      ];

      const escapeCsv = (val) => {
        if (val === null || val === undefined) return "";
        const s = String(val);
        if (/[",\n]/.test(s)) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      };

      const deriveReportType = (row) => {
        const raw = ((row && row.reportType) || (row && row.reportSubscription && row.reportSubscription.reportType) || "").toString().trim().toLowerCase();
        const map = { compact: "Compact", dynamic: "Personal", advanced: "Total", personal: "Personal", total: "Total" };
        return map[raw] || "";
      };

      const deriveReportAddons = (row) => {
        if (Array.isArray(row && row.reportAddons) && row.reportAddons.length) {
          return row.reportAddons.join(", ");
        }
        const addOns = row && row.reportSubscription && row.reportSubscription.addOns;
        if (addOns && typeof addOns === "object") {
          const derived = [];
          if (addOns.bodySummary) derived.push("Body Summary");
          if (addOns.reportSummary) derived.push("Report Summary");
          if (addOns.additionalTestRecommendation) derived.push("Test Recommendation");
          return derived.join(", ");
        }
        return "";
      };

      // Fetch ALL missing data with progress tracking
      console.log(`📊 Starting CSV export for ${rows.length} clients...`);
      const enrichedRows = await fetchAllDataForExport(rows);
      console.log(`✅ Data fetching complete for ${enrichedRows.length} clients`);

      // Build CSV
      const csvLines = [];
      csvLines.push(headers.map(escapeCsv).join(","));

      enrichedRows.forEach((row) => {
        const line = [
          row.customerName || row.displayName || "",
          Array.isArray(row.mailIds) ? row.mailIds.join(";") : (row.mailIds || ""),
          row.reportsSubscribed ?? "",
          row.count ?? "",
          row.feedbackRating || "Not Available", // Now has real data
          row.invoiceAmount || "Not Available",  // NEW field
          row.activityCount ?? "0",               // NEW field
          formatDateForExport(row.liveDate),
          formatDateForExport(row.startDate),
          formatDateForExport(row.endDate),
          row.subscriptionStatus || "",
          row.lisHisName || "Not Available",
          deriveServiceType(row) || "",
          row.reportType || deriveReportType(row) || "",
          Array.isArray(row.reportAddons) ? row.reportAddons.join(", ") : deriveReportAddons(row),
          deriveNumberOfTickets(row) || "",
          deriveNpsScore(row) || "",
          row.planType || "",
          row.clientType || "",
        ];
        csvLines.push(line.map(escapeCsv).join(","));
      });

      const csvContent = "\uFEFF" + csvLines.join("\n"); // BOM for Excel UTF-8
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const fileName = `subscriptions_${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${rows.length}-clients.csv`;
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(`📥 CSV downloaded: ${fileName}`);
    } catch (err) {
      console.error("Export failed:", err);
      alert.error("Failed to export CSV. Please try again.");
    } finally {
      // Reset states
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };

  const isValidSubscriptionEndDate = (subscriptionDate) => {
    const date = new Date();
    return new Date(subscriptionDate) > date;
  };

  const getClientCounts = async (
    clientDetails,
    productType,
    isPreviousCycle = false
  ) => {
    const startTime = performance.now();
    console.log(`🚀 Starting API call for ${clientDetails.org}-${clientDetails.center} at ${new Date().toISOString()}`);

    // Used for ID Token
    const idToken = localStorage.getItem("idTokenBilling");

    // For Getting User ID for sending in the API Call
    let clientData = [];
    try {
      clientData = JSON.parse(localStorage.getItem("loginDetails"))?.clientData || [];
    } catch (_) {
      clientData = [];
    }
    const selectedClientDetails = clientData.filter(
      (each) =>
        each.org === clientDetails.org && each.center === clientDetails.center
    )[0];
    const userId = selectedClientDetails?.userId;

    // Set loader immediately with a unique key to prevent race conditions
    const loaderKey = productType === "smartreport"
      ? (isPreviousCycle ? "prevCountLoader" : "loader")
      : "vizAppLoader";

    const requestId = `${clientDetails.org}-${clientDetails.center}-${Date.now()}`;
    console.log(`🔄 Setting loader for request ${requestId}`);

    setDataForTables((existingData) => {
      return existingData.map((eachData) => {
        if (
          eachData.org === clientDetails.org &&
          eachData.center === clientDetails.center
        ) {
          return { ...eachData, [loaderKey]: true, requestId: requestId };
        }
        return { ...eachData };
      });
    });

    // Configuration for API Call
    const configBody = {
      headers: {
        authorization: idToken,
      },
      method: "get",
      url: `https://api.niroggyan.com/billing/${productType}/counts?query_on=counts&query_by=subscription&userId=${userId}&cycle=${isPreviousCycle ? "previous" : ""
        }`,
      validateStatus: function (status) {
        return (status >= 200) && (status < 510);
      },
    };

    const attemptRequest = async () => {
      try {
        return await axios(configBody);
      } catch (err) {
        throw err;
      }
    };

    // First attempt
    try {
      const response = await attemptRequest();
      const apiTime = performance.now();
      console.log(`📡 API Response received in ${(apiTime - startTime).toFixed(2)}ms for ${requestId}`);

      try {
        console.groupCollapsed(
          `API raw • GET counts (${productType}${isPreviousCycle ? ": previous" : ""}) • ${requestId}`
        );
        console.log("🔗 API URL:", configBody.url);
        console.log("📊 Status Code:", response?.status);
        console.log("📋 Request Headers:", configBody.headers);
        console.log("📤 Request Payload:", {
          query_on: "counts",
          query_by: "subscription",
          userId: userId,
          cycle: isPreviousCycle ? "previous" : ""
        });
        console.log("📥 Raw Response:", response?.data);
        console.log("📄 Formatted JSON:\n" + JSON.stringify(response?.data, null, 2));

        // Enhanced logging for previous count specifically
        if (isPreviousCycle) {
          console.log("🔄 PREVIOUS CYCLE DATA ANALYSIS:");
          console.log("✅ Success Status:", response?.data?.isSuccess);
          console.log("📊 Data Array:", response?.data?.data);
          if (response?.data?.data && response?.data?.data[0]) {
            console.log("🔢 Previous Count Value:", response?.data?.data[0].count);
            console.log("📈 Count Type:", typeof response?.data?.data[0].count);
          } else {
            console.log("⚠️ No data array found in response");
          }
        }

        console.groupEnd();
      } catch (_) { }

      if (response.data.isSuccess) {
        const count = response.data.data[0].count;
        console.log(`✅ Processing count ${count} for ${requestId}`);

        // Use setTimeout to ensure immediate UI update (compatible with older React versions)
        setTimeout(() => {
          setDataForTables((existingData) => {
            const updatedData = existingData.map((eachData) => {
              if (
                eachData.org === clientDetails.org &&
                eachData.center === clientDetails.center &&
                eachData.requestId === requestId // Only update if it's the same request
              ) {
                if (productType === "smartreport") {
                  if (isPreviousCycle) {
                    return { ...eachData, previousCount: count, prevCountLoader: false, requestId: null };
                  } else {
                    return { ...eachData, count: count, loader: false, requestId: null };
                  }
                } else {
                  return { ...eachData, vizAppCount: count, vizAppLoader: false, requestId: null };
                }
              }
              return { ...eachData };
            });

            const uiUpdateTime = performance.now();
            console.log(`🎯 UI updated in ${(uiUpdateTime - apiTime).toFixed(2)}ms for ${requestId}`);
            console.log(`⚡ Total time: ${(uiUpdateTime - startTime).toFixed(2)}ms for ${requestId}`);
            return updatedData;
          });
        }, 0);
      } else {
        throw new Error(`API returned isSuccess: false for ${requestId}`);
      }
    } catch (e1) {
      // Retry once silently
      console.log(`🔁 Retry attempt for ${requestId} due to error:`, e1?.message);
      try {
        const response = await attemptRequest();
        const apiTime = performance.now();
        console.log(`📡 API Response (retry) in ${(apiTime - startTime).toFixed(2)}ms for ${requestId}`);

        if (response.data.isSuccess) {
          const count = response.data.data[0].count;
          setTimeout(() => {
            setDataForTables((existingData) => {
              return existingData.map((eachData) => {
                if (
                  eachData.org === clientDetails.org &&
                  eachData.center === clientDetails.center &&
                  eachData.requestId === requestId
                ) {
                  if (productType === "smartreport") {
                    if (isPreviousCycle) {
                      return { ...eachData, previousCount: count, prevCountLoader: false, requestId: null };
                    } else {
                      return { ...eachData, count: count, loader: false, requestId: null };
                    }
                  } else {
                    return { ...eachData, vizAppCount: count, vizAppLoader: false, requestId: null };
                  }
                }
                return { ...eachData };
              });
            });
          }, 0);
        } else {
          throw new Error(`API returned isSuccess: false on retry for ${requestId}`);
        }
      } catch (e2) {
        const errorTime = performance.now();
        console.log(`❌ Final failure fetching Counts for ${requestId} after ${(errorTime - startTime).toFixed(2)}ms:`, e2?.message);

        // Reset loader on final error (no alert per requirement)
        setDataForTables((existingData) => {
          return existingData.map((eachData) => {
            if (
              eachData.org === clientDetails.org &&
              eachData.center === clientDetails.center &&
              eachData.requestId === requestId
            ) {
              return { ...eachData, [loaderKey]: false, requestId: null };
            }
            return { ...eachData };
          });
        });
      }
    }
  };

  // Function to get client feedback rating (averageFeedback -> "X.X/5" or "Not Available")

  // New function for getting previous month counts
  const getClientCountsForPreviousMonth = async (clientDetails) => {
    // Used for ID Token
    const idToken = localStorage.getItem("idTokenBilling");

    // For Getting User ID for sending in the API Call
    let clientData = [];
    try {
      clientData = JSON.parse(localStorage.getItem("loginDetails"))?.clientData || [];
    } catch (_) {
      clientData = [];
    }
    const selectedClientDetails = clientData.filter(
      (each) =>
        each.org === clientDetails.org && each.center === clientDetails.center
    )[0];
    const userId = selectedClientDetails?.userId;

    // Get previous month information
    const { startDate, endDate, displayDate } = getPreviousMonthInfo();

    // Set loading state
    setDataForTables((existingData) => {
      return existingData.map((eachData) => {
        if (
          eachData.org === clientDetails.org &&
          eachData.center === clientDetails.center
        ) {
          return { ...eachData, prevMonthLoader: true };
        }
        return { ...eachData };
      });
    });

    // Use the same API pattern as existing previous count but with month parameter
    // Configuration for API Call - using same pattern as existing previous count API
    const configBody = {
      headers: {
        authorization: idToken,
      },
      method: "get",
      url: `https://api.niroggyan.com/billing/smartreport/counts?query_on=counts&query_by=subscription&userId=${userId}&cycle=previous&month=${startDate.split('-')[0]}-${startDate.split('-')[1]}`,
      validateStatus: function (status) {
        return (status >= 200) && (status < 510);
      },
    };

    try {
      const response = await axios(configBody);

      // Immediate UI update after API response
      console.log("🚀 API Response received, updating UI immediately...");

      try {
        console.groupCollapsed(
          `📅 Previous Month Count API • ${displayDate}`
        );
        console.log("🔗 API URL:", configBody.url);
        console.log("📊 Status Code:", response?.status);
        console.log("📋 Request Headers:", configBody.headers);
        console.log("📤 Request Payload:", {
          query_on: "counts",
          query_by: "subscription",
          userId: userId,
          cycle: "previous",
          month: `${startDate.split('-')[0]}-${startDate.split('-')[1]}`
        });
        console.log("📅 Previous Month Info:", {
          startDate: startDate,
          endDate: endDate,
          displayDate: displayDate,
          monthParam: `${startDate.split('-')[0]}-${startDate.split('-')[1]}`
        });
        console.log("📥 Raw Response:", response?.data);
        console.log("📄 Formatted JSON:\n" + JSON.stringify(response?.data, null, 2));

        // Enhanced logging for previous month count
        console.log("🔄 PREVIOUS MONTH DATA ANALYSIS:");
        console.log("✅ Success Status:", response?.data?.isSuccess);
        console.log("📊 Data Array:", response?.data?.data);
        if (response?.data?.data && response?.data?.data[0]) {
          console.log("🔢 Previous Month Count Value:", response?.data?.data[0].count);
          console.log("📈 Count Type:", typeof response?.data?.data[0].count);
          console.log("📅 Display Format:", `${displayDate} : ${response?.data?.data[0].count}`);
        } else {
          console.log("⚠️ No data array found in response");
        }

        console.groupEnd();
      } catch (_) { }

      if (response.data.isSuccess) {
        const count = response.data.data[0]?.count || 0;
        const formattedCount = `${displayDate} : ${count}`;

        console.log("📊 Previous Month Data:", response.data.data);
        console.log("🔢 Previous Month Count:", count);
        console.log("📅 Final Display Format:", formattedCount);

        // Force immediate UI update
        setDataForTables((existingData) => {
          const updatedData = existingData.map((eachData) => {
            if (
              eachData.org === clientDetails.org &&
              eachData.center === clientDetails.center
            ) {
              return {
                ...eachData,
                previousMonthCount: formattedCount,
                previousMonthCountRaw: count,
                prevMonthLoader: false
              };
            }
            return { ...eachData };
          });

          console.log("✅ UI State updated with count:", formattedCount);
          return updatedData;
        });
      } else {
        throw new Error();
      }
    } catch (e) {
      console.log("❌ Error in fetching Previous Month Counts");
      window.alert("Error in fetching Previous Month Counts");
    } finally {
      // Only reset loader if it's still true (avoid unnecessary updates)
      setDataForTables((existingData) => {
        return existingData.map((eachData) => {
          if (
            eachData.org === clientDetails.org &&
            eachData.center === clientDetails.center &&
            eachData.prevMonthLoader === true
          ) {
            return { ...eachData, prevMonthLoader: false };
          }
          return { ...eachData };
        });
      });
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: () => (
          <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <span style={{ pointerEvents: "none" }}>Client Name</span>
            <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "90px" }}>
              {alertsLoaded ? (
                <>
                  <select
                    value={clientAlertFilter}
                    onChange={(e) => setClientAlertFilter(e.target.value)}
                    title="Filter by Alert Type"
                    style={{
                      fontSize: "11px",
                      padding: "2px 20px 2px 6px",
                      borderRadius: "5px",
                      border: "1px solid #dde0e1",
                      background: "#f8f9fa",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: "#334155",
                      height: "22px",
                      width: "100%",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      appearance: "none",
                    }}
                  >
                    <option value="">All</option>
                    <option value="low_usage">⚠ Below Avg</option>
                    <option value="critical">🔴 No Reports</option>
                  </select>
                  <span style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", fontSize: "8px", color: "#94a3b8", pointerEvents: "none" }}>
                    ▼
                  </span>
                </>
              ) : (
                <span style={{ fontSize: "9px", color: "#94a3b8", fontStyle: "italic" }}>Loading alerts...</span>
              )}
            </div>
          </div>
        ),
        headerTitle: "Client Name",
        id: "customerName",
        accessor: (row) => row.customerName,
        disableSortBy: false,
        sortLabelConfig: { asc: "A → Z", desc: "Z → A" },
        showMobile: true,
        sortType: (rowA, rowB, columnId) => {
          const a = (rowA.values[columnId] || "").toString().trim().toLowerCase();
          const b = (rowB.values[columnId] || "").toString().trim().toLowerCase();
          return a.localeCompare(b);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { original } = row;
          const customerName = original.customerName || "";
          return (
            <ClientNameCellWithLowUsageWarning
              client={original}
              customerName={customerName}
              isMobile={isMobile}
              refreshTick={refreshTick}
            />
          );
        },
      },
      {
        Header: "Org",
        accessor: "org",
        disableSortBy: true,
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          return (
            <div style={{ width: "80px", wordWrap: "break-word" }}>
              {values.org}
            </div>
          );
        },
      },
      {
        Header: "Center",
        accessor: "center",
        disableSortBy: true,
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          return (
            <div style={{ width: "80px", wordWrap: "break-word" }}>
              {values.center}
            </div>
          );
        },
      },
      {
        Header: "Mail ID",
        accessor: "mailIds",
        disableSortBy: true,
        sortType: (rowA, rowB, columnId) => {
          const a = (rowA.values[columnId] || []).join(",").toLowerCase();
          const b = (rowB.values[columnId] || []).join(",").toLowerCase();
          return a.localeCompare(b);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          return (
            <div style={{ width: "190px", wordWrap: "break-word" }}>
              {values.mailIds.join(",")}
            </div>
          );
        },
      },
      {
        Header: "Reports Subscribed",
        accessor: "reportsSubscribed",
        disableSortBy: false,
        sortLabelConfig: { asc: "Low → High", desc: "High → Low" },
        sortType: (rowA, rowB, columnId) => {
          const a = Number(rowA.values[columnId] || 0);
          const b = Number(rowB.values[columnId] || 0);
          return a - b;
        },
      },
      // For Creating Loader
      {
        Header: "pastCount-loader",
        accessor: "prevCountLoader",
      },
      {
        Header: "loader",
        accessor: "loader",
      },
      {
        Header: "vizApp-loader",
        accessor: "vizAppLoader",
      },
      {
        Header: "prevMonth-loader",
        accessor: "prevMonthLoader",
      },
      {
        Header: "Reports consumed (previous)",
        accessor: "previousCount",
        disableSortBy: true,
        sortType: (rowA, rowB, columnId) => {
          const a = Number(rowA.values[columnId] || 0);
          const b = Number(rowB.values[columnId] || 0);
          return a - b;
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          return values.previousCount ? (
            <div
              style={{
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                textAlign: "center",
              }}
            >
              <span>{values.previousCount}</span>
            </div>
          ) : (
            <button
              className="btn btn-primary btn-sm w-100"
              style={{
                fontSize: isMobile ? "12px" : "13px",
                padding: isMobile ? "3px 6px" : "4px 8px",
              }}
              onClick={async () => {
                await getClientCounts(values, "smartreport", true);
              }}
            >
              {values.prevCountLoader ? (
                <Spinner animation="border" variant="white" size="sm" />
              ) : (
                "Get Count"
              )}
            </button>
          );
        },
      },
      {
        Header: "Reports consumed (previous month)",
        accessor: "previousMonthCount",
        disableSortBy: true,
        sortType: (rowA, rowB, columnId) => {
          const a = Number(rowA.values[columnId] || 0);
          const b = Number(rowB.values[columnId] || 0);
          return a - b;
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          return values.previousMonthCount ? (
            <div
              style={{
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                textAlign: "center",
                backgroundColor: "#E8F4FD",
                color: "#1E40AF"
              }}
            >
              <span style={{ fontSize: "13px", fontWeight: "500" }}>
                {values.previousMonthCount}
              </span>
            </div>
          ) : (
            <button
              className="btn btn-primary btn-sm w-100"
              style={{
                fontSize: isMobile ? "12px" : "13px",
                padding: isMobile ? "3px 6px" : "4px 8px",
              }}
              disabled={!!values.prevMonthLoader}
              onClick={async () => {
                if (values.prevMonthLoader) return;
                await getClientCountsForPreviousMonth(values);
              }}
            >
              {values.prevMonthLoader ? (
                <Spinner animation="border" variant="white" size="sm" />
              ) : (
                "Get Count"
              )}
            </button>
          );
        },
      },

      {
        Header: "Average Feedback",
        accessor: "feedbackRating",
        disableSortBy: false,
        sortLabelConfig: { asc: "Low → High", desc: "High → Low" },
        sortType: (rowA, rowB, columnId) => {
          const parse = (val) => {
            if (!val || val === "Not Available" || val === "N/A" || val === "") return -1;
            const match = val.toString().match(/(\d+\.?\d*)\/5/);
            return match ? parseFloat(match[1]) : -1;
          };
          return parse(rowA.values[columnId]) - parse(rowB.values[columnId]);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          const original = row.original || values;
          return (
            <div
              style={{
                width: "100%",
                fontSize: isMobile ? "12px" : "13px",
              }}
            >
              <FeedbackRatingCell
                client={original}
                tick={refreshTick}
                onResolved={(resolved) => {
                  setDataForTables((prev) =>
                    (prev || []).map((r) =>
                      r.org === original.org && r.center === original.center
                        ? { ...r, feedbackRating: resolved }
                        : r
                    )
                  );
                }}
              />
            </div>
          );
        },
      },
      {
        Header: "Reports consumed",
        accessor: "count",
        disableSortBy: true,
        sortType: (rowA, rowB, columnId) => {
          const a = Number(rowA.values[columnId] || 0);
          const b = Number(rowB.values[columnId] || 0);
          return a - b;
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          return values.count ? (
            <div
              style={{
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                textAlign: "center",
              }}
            >
              <span>{values.count}</span>
            </div>
          ) : (
            <button
              className="btn btn-primary btn-sm w-100"
              style={{
                fontSize: isMobile ? "12px" : "13px",
                padding: isMobile ? "3px 6px" : "4px 8px",
              }}
              disabled={!!values.loader}
              onClick={async () => {
                if (values.loader) return;
                await getClientCounts(values, "smartreport", false);
              }}
            >
              {values.loader ? (
                <Spinner animation="border" variant="white" size="sm" />
              ) : (
                "Get Count"
              )}
            </button>
          );
        },
      },
      {
        Header: "Invoice Amout (current subscription)",
        accessor: "invoiceAmountCurrent",
        disableSortBy: false,
        sortLabelConfig: { asc: "Low → High", desc: "High → Low" },
        sortType: (rowA, rowB) => {
          const getAmount = (row) => {
            const key = getInvoiceKey(row.original);
            const raw = key ? invoiceAmounts[key] : null;
            if (!raw || raw === "Loading..." || raw === "Not available" || raw === "Not Available") return -1;
            if (typeof raw === "object" && raw.amount !== undefined) return Number(raw.amount) || 0;
            const num = parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
            return isNaN(num) ? -1 : num;
          };
          return getAmount(rowA) - getAmount(rowB);
        },
        /**
         * Invoice Amount Cell Component
         * 
         * Displays invoice amounts with conditional styling:
         * - Green background (#D1FFE9) for actual invoice amounts
         * - Gray background (#F3F4F6) for "Not available"
         * - No background for "Loading..." state
         * 
         * Uses composite key (clientId|org|center) to retrieve amounts from state,
         * which prevents issues with duplicate clientIds.
         */
        Cell: (cell) => {
          const { row } = cell;
          const { values, original } = row;

          // Get client data from row (prioritize original since clientId is not an accessor)
          const clientData = original || row.original || values;

          // Generate composite key for unique identification (handles duplicate clientIds)
          const invoiceKey = getInvoiceKey(clientData);

          // Retrieve invoice amount from state using composite key
          // Check raw value to distinguish "not loaded" (undefined) from "actively loading" ("Loading...")
          const rawAmount = invoiceKey ? invoiceAmounts[invoiceKey] : undefined;
          const amount = rawAmount || "Loading...";

          // STATUS-BASED: Show "Load Data" button or spinner for non-live clients
          const isLive = isLiveSubscriptionRow(clientData);
          if (!isLive && (!rawAmount || rawAmount === "__manual__" || rawAmount === "Loading...")) {
            // If actively loading (key exists as "Loading..."), show spinner
            const isActivelyLoading = rawAmount === "Loading...";
            return (
              <button
                className="btn btn-primary btn-sm w-100"
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  padding: isMobile ? "3px 6px" : "4px 8px",
                }}
                disabled={isActivelyLoading}
                onClick={async () => {
                  if (isActivelyLoading) return;
                  await loadInvoiceForSingleClient(clientData);
                }}
              >
                {isActivelyLoading ? (
                  <Spinner animation="border" variant="white" size="sm" />
                ) : (
                  "Load Data"
                )}
              </button>
            );
          }

          // Determine styling based on amount value
          const isAmount = amount !== "Loading..." && amount !== "Not available" && amount !== "Not Available";
          const isNotAvailable = amount === "Not available" || amount === "Not Available";

          // Style for actual invoice amounts (green background)
          if (isAmount) {
            return (
              <div
                style={{
                  backgroundColor: "#D1FFE9",
                  color: "#2DAC6E",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  minHeight: "32px",
                  textAlign: "center",
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "500",
                }}
              >
                {/* Currency-aware display: INR → remove GST (÷1.18); non-INR → show as-is */}
                {(() => {
                  const rawData = invoiceKey ? invoiceAmounts[invoiceKey] : null;
                  // Handle legacy cache entries that are plain strings
                  if (!rawData || typeof rawData !== 'object') {
                    const num = parseFloat(String(amount).replace(/[^0-9.-]/g, ''));
                    return isNaN(num) ? amount : `₹${(num / 1.18).toFixed(2)}`;
                  }
                  const { amount: num, currencyCode, currencySymbol } = rawData;
                  const isINR = currencyCode === 'INR';
                  // INR: always divide by 1.18 to strip GST
                  // Non-INR (USD etc.): show as-is, no GST adjustment
                  const displayAmount = isINR
                    ? (num / 1.18).toFixed(2)
                    : Number(num).toFixed(2);
                  const symbol = currencySymbol || (isINR ? '₹' : '');
                  return `${symbol}${displayAmount}`;
                })()}
              </div>
            );
          }

          // Style for "Not available" (gray background)
          if (isNotAvailable) {
            return (
              <div
                style={{
                  backgroundColor: "#F3F4F6",
                  color: "#6B7280",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  minHeight: "32px",
                  textAlign: "center",
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "500",
                }}
              >
                {amount}
              </div>
            );
          }

          // Style for loading state (no background, just text)
          return (
            <div
              style={{
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                minHeight: "32px",
                textAlign: "center",
                fontSize: isMobile ? "12px" : "13px",
              }}
            >
              {amount}
            </div>
          );
        },
      },
      {
        Header: "Per Report Rate",
        accessor: "perReportRate",
        disableSortBy: false,
        sortLabelConfig: { asc: "Low → High", desc: "High → Low" },
        sortType: (rowA, rowB) => {
          const getRate = (row) => {
            const key = getInvoiceKey(row.original);
            const raw = key ? perReportRates[key] : null;
            if (!raw || raw === "Loading..." || raw === "Not available" || raw === "Not Available") return -1;
            if (typeof raw === "object" && raw.amount !== undefined) return Number(raw.amount) || 0;
            const num = parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
            return isNaN(num) ? -1 : num;
          };
          return getRate(rowA) - getRate(rowB);
        },
        /**
         * Per Report Rate Cell Component
         * 
         * Displays per-report rate (latest invoice ÷ 1.18 ÷ reports subscribed) with conditional styling:
         * - Green background (#D1FFE9) for actual rates
         * - Gray background (#F3F4F6) for "Not available"
         * - No background for "Loading..." state
         * 
         * Uses composite key (clientId|org|center) to retrieve rates from state,
         * which prevents issues with duplicate clientIds.
         */
        Cell: (cell) => {
          const { row } = cell;
          const { values, original } = row;

          // Get client data from row (prioritize original since clientId is not an accessor)
          const clientData = original || row.original || values;

          // Generate composite key for unique identification (handles duplicate clientIds)
          const rateKey = getInvoiceKey(clientData);

          // Retrieve per-report rate from state using composite key
          // Check raw value to distinguish "not loaded" (undefined) from "actively loading" ("Loading...")
          const rawRate = rateKey ? perReportRates[rateKey] : undefined;
          const rate = rawRate || "Loading...";

          // STATUS-BASED: Show "Load Data" button or spinner for non-live clients
          const isLive = isLiveSubscriptionRow(clientData);
          if (!isLive && (!rawRate || rawRate === "__manual__" || rawRate === "Loading...")) {
            // If actively loading (key exists as "Loading..."), show spinner
            const isActivelyLoading = rawRate === "Loading...";
            return (
              <button
                className="btn btn-primary btn-sm w-100"
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  padding: isMobile ? "3px 6px" : "4px 8px",
                }}
                disabled={isActivelyLoading}
                onClick={async () => {
                  if (isActivelyLoading) return;
                  await loadInvoiceForSingleClient(clientData);
                }}
              >
                {isActivelyLoading ? (
                  <Spinner animation="border" variant="white" size="sm" />
                ) : (
                  "Load Data"
                )}
              </button>
            );
          }

          // Determine styling based on rate value
          const isRate = rate !== "Loading..." && rate !== "Not available" && rate !== "Not Available";
          const isNotAvailable = rate === "Not available" || rate === "Not Available";

          // Style for actual per-report rates (green background)
          if (isRate) {
            return (
              <div
                style={{
                  backgroundColor: "#D1FFE9",
                  color: "#2DAC6E",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  minHeight: "32px",
                  textAlign: "center",
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "500",
                }}
              >
                {(() => {
                  const rawData = rateKey ? perReportRates[rateKey] : null;
                  // Handle legacy cache entries that are plain strings
                  if (!rawData || typeof rawData !== 'object') {
                    const num = parseFloat(String(rate).replace(/[^0-9.-]/g, ''));
                    return isNaN(num) ? rate : `₹${num.toFixed(2)}`;
                  }
                  const { amount: num, currencyCode, currencySymbol } = rawData;
                  const symbol = currencySymbol || (currencyCode === 'INR' ? '₹' : '');
                  return `${symbol}${num.toFixed(2)}`;
                })()}
              </div>
            );
          }

          // Style for "Not available" (gray background)
          if (isNotAvailable) {
            return (
              <div
                style={{
                  backgroundColor: "#F3F4F6",
                  color: "#6B7280",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  minHeight: "32px",
                  textAlign: "center",
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "500",
                }}
              >
                {rate}
              </div>
            );
          }

          // Style for loading state (no background, just text)
          return (
            <div
              style={{
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                minHeight: "32px",
                textAlign: "center",
                fontSize: isMobile ? "12px" : "13px",
              }}
            >
              {rate}
            </div>
          );
        },
      },
      {
        Header: "Activity Count",
        accessor: "activityCount",
        disableSortBy: false,
        sortLabelConfig: { asc: "Low → High", desc: "High → Low" },
        sortType: (rowA, rowB) => {
          const getCount = (row) => {
            const key = getInvoiceKey(row.original);
            const raw = key ? activityCounts[key] : null;
            if (!raw || raw === "Loading...") return -1;
            const num = Number(raw);
            return isNaN(num) ? -1 : num;
          };
          return getCount(rowA) - getCount(rowB);
        },
        /**
         * Activity Count Cell Component
         * 
         * Displays activity counts with conditional styling:
         * - Green background (#D1FFE9) for actual counts
         * - Gray background (#F3F4F6) for "0" (includes errors and empty data)
         * - No background for "Loading..." state
         * 
         * Uses composite key (clientId|org|center) to retrieve counts from state,
         * which prevents issues with duplicate clientIds.
         */
        Cell: (cell) => {
          const { row } = cell;
          const { values, original } = row;

          // Get client data from row (prioritize original since clientId is not an accessor)
          const clientData = original || row.original || values;

          // Generate composite key for unique identification (handles duplicate clientIds)
          const activityKey = getInvoiceKey(clientData);

          // Retrieve activity count from state using composite key
          // Check raw value to distinguish "not loaded" (undefined) from "actively loading" ("Loading...")
          const rawCount = activityKey ? activityCounts[activityKey] : undefined;
          const count = rawCount || "Loading...";

          // STATUS-BASED: Show "Load Data" button or spinner for non-live clients
          const isLive = isLiveSubscriptionRow(clientData);
          if (!isLive && (!rawCount || rawCount === "__manual__" || rawCount === "Loading...")) {
            // If actively loading (key exists as "Loading..."), show spinner
            const isActivelyLoading = rawCount === "Loading...";
            return (
              <button
                className="btn btn-primary btn-sm w-100"
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  padding: isMobile ? "3px 6px" : "4px 8px",
                }}
                disabled={isActivelyLoading}
                onClick={async () => {
                  if (isActivelyLoading) return;
                  await loadActivityForSingleClient(clientData);
                }}
              >
                {isActivelyLoading ? (
                  <Spinner animation="border" variant="white" size="sm" />
                ) : (
                  "Load Data"
                )}
              </button>
            );
          }

          // Determine styling based on count value
          const isCount = count !== "Loading..." && count !== "0";
          const isZero = count === "0";
          const isLoading = count === "Loading...";

          // Style for actual counts (green background)
          if (isCount) {
            return (
              <div
                style={{
                  backgroundColor: "#D1FFE9",
                  color: "#2DAC6E",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  minHeight: "32px",
                  textAlign: "center",
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "500",
                }}
              >
                {count}
              </div>
            );
          }

          // Style for zero count or loading (gray background for zero, no background for loading)
          if (isZero) {
            return (
              <div
                style={{
                  backgroundColor: "#F3F4F6",
                  color: "#6B7280",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  minHeight: "32px",
                  textAlign: "center",
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "500",
                }}
              >
                {count}
              </div>
            );
          }

          // Style for loading state (no background, just text)
          return (
            <div
              style={{
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                minHeight: "32px",
                textAlign: "center",
                fontSize: isMobile ? "12px" : "13px",
              }}
            >
              {count}
            </div>
          );
        },
      },
      {
        Header: "Vizapp Count",
        accessor: "vizAppCount",
        disableSortBy: true,
        sortType: (rowA, rowB, columnId) => {
          const a = Number(rowA.values[columnId] || 0);
          const b = Number(rowB.values[columnId] || 0);
          return a - b;
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          return values.vizAppCount ? (
            <div
              style={{
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                textAlign: "center",
              }}
            >
              {values.vizAppCount}
            </div>
          ) : (
            <button
              className="btn btn-primary w-100"
              onClick={async () => {
                await getClientCounts(values, "vizapp");
              }}
            >
              {values.vizAppLoader ? (
                <Spinner animation="border" variant="white" size="sm" />
              ) : (
                "Get Count"
              )}
            </button>
          );
        },
      },
      {
        Header: "Live Date",
        accessor: "liveDate",
        disableSortBy: false,
        sortLabelConfig: { asc: "Old → New", desc: "New → Old" },
        sortType: (rowA, rowB, columnId) => {
          const toNum = (val) => {
            if (!val || typeof val !== "string") return 0;
            const raw = val.split(" ")[0];
            const parts = raw.split("-");
            if (parts.length !== 3) return 0;
            let y, m, d;
            if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; } else { d = parts[0]; m = parts[1]; y = parts[2]; }
            if (!y || !m || !d) return 0;
            return Number(`${String(y).padStart(4, '0')}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`);
          };
          return toNum(rowA.values[columnId]) - toNum(rowB.values[columnId]);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          
          const notAvailableUI = (
            <div style={{ color: "#94A3B8", fontStyle: "italic", whiteSpace: "nowrap", textAlign: "center" }}>
              Not Available
            </div>
          );

          if (!values.liveDate ||
            values.liveDate === "undefined" ||
            values.liveDate === "null" ||
            values.liveDate.includes("undefined") ||
            values.liveDate === "" ||
            typeof values.liveDate !== "string") {
            return notAvailableUI;
          }
          const raw = values.liveDate.split(" ")[0];
          const [y, m, d] = raw.split("-");
          // Check if any part is undefined or invalid
          if (!y || !m || !d || y === "undefined" || m === "undefined" || d === "undefined") {
            return notAvailableUI;
          }
          const yy = (y || "").slice(0);
          return (
            <div
              style={{
                backgroundColor: "#D1FFE9",
                borderRadius: "5px",
                border: "none",
                width: "100%",
                padding: "5px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ color: "#2DAC6E" }}>
                {`${d}-${m}-${yy}`}
              </span>
            </div>
          );
        },
      },
      {
        Header: "Subscription Start Date",
        accessor: "startDate",
        disableSortBy: false,
        sortLabelConfig: { asc: "Old → New", desc: "New → Old" },
        sortType: (rowA, rowB, columnId) => {
          const toNum = (val) => {
            if (!val || typeof val !== "string") return 0;
            const raw = val.split(" ")[0];
            const parts = raw.split("-");
            if (parts.length !== 3) return 0;
            let y, m, d;
            if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; } else { d = parts[0]; m = parts[1]; y = parts[2]; }
            if (!y || !m || !d) return 0;
            return Number(`${String(y).padStart(4, '0')}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`);
          };
          return toNum(rowA.values[columnId]) - toNum(rowB.values[columnId]);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          if (!values.startDate ||
            values.startDate === "undefined" ||
            values.startDate === "null" ||
            values.startDate.includes("undefined") ||
            values.startDate === "" ||
            typeof values.startDate !== "string") {
            return "";
          }
          const raw = values.startDate.split(" ")[0];
          const [y, m, d] = raw.split("-");
          // Check if any part is undefined or invalid
          if (!y || !m || !d || y === "undefined" || m === "undefined" || d === "undefined") {
            return "";
          }
          const yy = (y || "").slice(0);
          return `${d}-${m}-${yy}`;
        },
      },
      {
        Header: "Subscription End Date",
        accessor: "endDate",
        disableSortBy: false,
        showMobile: true,
        sortLabelConfig: { asc: "Old → New", desc: "New → Old" },
        sortType: (rowA, rowB, columnId) => {
          const toNum = (val) => {
            if (!val || typeof val !== "string") return 0;
            const raw = val.split(" ")[0];
            const parts = raw.split("-");
            if (parts.length !== 3) return 0;
            let y, m, d;
            if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; } else { d = parts[0]; m = parts[1]; y = parts[2]; }
            if (!y || !m || !d) return 0;
            return Number(`${String(y).padStart(4, '0')}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`);
          };
          return toNum(rowA.values[columnId]) - toNum(rowB.values[columnId]);
        },
        headerClassName: "alignment",
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;
          //   console.log('values', isValidSubscriptionEndDate(values.endDate));
          if (!values.endDate ||
            values.endDate === "undefined" ||
            values.endDate === "null" ||
            values.endDate.includes("undefined") ||
            values.endDate === "" ||
            typeof values.endDate !== "string") {
            return "";
          }
          if (isValidSubscriptionEndDate(values.endDate)) {
            const raw = values.endDate.split(" ")[0];
            const [y, m, d] = raw.split("-");
            // Check if any part is undefined or invalid
            if (!y || !m || !d || y === "undefined" || m === "undefined" || d === "undefined") {
              return "";
            }
            const yy = (y || "").slice(0);
            return (
              <div
                style={{
                  backgroundColor: "#D1FFE9",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  textAlign: "center",
                }}
              >
                <span style={{ color: "#2DAC6E" }}>
                  {`${d}-${m}-${yy}`}
                </span>
              </div>
            );
          } else {
            const raw = values.endDate.split(" ")[0];
            const [y, m, d] = raw.split("-");
            // Check if any part is undefined or invalid
            if (!y || !m || !d || y === "undefined" || m === "undefined" || d === "undefined") {
              return "";
            }
            const yy = (y || "").slice(0);
            return (
              <div
                style={{
                  backgroundColor: "#FFDBD7",
                  borderRadius: "5px",
                  border: "none",
                  width: "100%",
                  padding: "5px",
                  textAlign: "center",
                }}
              >
                <span style={{ color: "#E3695E" }}>
                  {`${d}-${m}-${yy}`}
                </span>
              </div>
            );
          }
        },
      },
      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="text-center">Subscription Status</span>
            </div>
            <select
              value={subscriptionStatusFilter}
              onChange={(e) => setSubscriptionStatusFilter(e.target.value)}
              // Don't stop propagation here so header retains sort toggle behavior
              style={{
                fontSize: "12px",
                padding: "2px 6px",
                borderRadius: "6px",
                border: "1px solid #dde0e1",
                background: "#f8f9fa",
              }}
              title="Filter by Subscription Status"
            >
              <option value="">All</option>
              <option value="live">LIVE</option>
              <option value="inactive">INACTIVE</option>
              <option value="on hold">HOLD</option>
            </select>
          </div>
        ),
        headerTitle: "Subscription Status",
        accessor: "subscriptionStatus",
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;

          if (values.subscriptionStatus === "live") {
            return (
              <div
                style={{
                  backgroundColor: "#D1FFE9",
                  borderRadius: "5px",
                  border: "none",
                  width: isMobile ? "80%" : "100%",
                  padding: "5px",
                  textAlign: "center",
                  margin: "auto",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                <span style={{ color: "#2DAC6E", textTransform: "Capitalize" }}>
                  {values.subscriptionStatus}
                </span>
              </div>
            );
          } else {
            return (
              <div
                style={{
                  backgroundColor: "#FFDBD7",
                  borderRadius: "5px",
                  border: "none",
                  width: isMobile ? "80%" : "100%",
                  padding: "3px",
                  textAlign: "center",
                  margin: "auto",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                <span style={{ color: "#E3695E", textTransform: "Capitalize" }}>
                  {values.subscriptionStatus}
                </span>
              </div>
            );
          }
        },
        showMobile: true,
      },
      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>LIS/HIS Name</span>
            </div>
            <CustomFilterDropdown
              value={lisHisNameFilter}
              options={["", ...dynamicLisHisNameOptions, "Not Available"]}
              onChange={(val) => setLisHisNameFilter(val)}
              title="Filter by LIS/HIS Name"
            />
          </div>
        ),
        headerTitle: "LIS/HIS Name",
        accessor: "lisHisName",
        disableSortBy: true,
        Cell: (cell) => {
          const { row } = cell;
          const val = row?.original?.lisHisName;
          return (
            <div
              style={{
                width: "140px",
                wordWrap: "break-word",
                textAlign: "center",
                margin: "auto",
                color: val ? "inherit" : "#94A3B8",
                fontStyle: val ? "normal" : "italic",
              }}
            >
              {val || "Not Available"}
            </div>
          );
        },
      },
      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>Codebase Tag</span>
            </div>
            <CustomFilterDropdown
              value={codebaseTagFilter}
              options={["", ...dynamicCodebaseTagOptions, "Not Available"]}
              onChange={(val) => setCodebaseTagFilter(val)}
              title="Filter by Codebase Tag"
            />
          </div>
        ),
        headerTitle: "Codebase Tag",
        accessor: "codebaseTag",
        disableSortBy: true,
        Cell: (cell) => {
          const { row } = cell;
          const rawValue = row?.original?.codebaseTag;
          const codebaseTag = typeof rawValue === "string" ? rawValue.trim() : "";
          return (
            <div style={{ width: "120px", wordWrap: "break-word" }}>
              {codebaseTag || "Not Available"}
            </div>
          );
        },
      },
      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>Service Type</span>
            </div>
            <select
              value={serviceTypeFilter || ""}
              onChange={(e) => {
                const val = e.target.value;
                setServiceTypeFilter(val);
              }}
              style={{
                fontSize: "12px",
                padding: "2px 6px",
                borderRadius: "6px",
                border: "1px solid #dde0e1",
                background: "#f8f9fa",
              }}
              title="Filter by Service Type"
            >
              <option value="">All</option>
              <option value="API">API</option>
              <option value="Portal">Portal</option>
              <option value="Not Available">Not Available</option>
            </select>
          </div>
        ),
        headerTitle: "Service Type",
        accessor: (row) => deriveServiceType(row),
        id: "serviceTypeDisplay",
        disableSortBy: false,
        sortType: (rowA, rowB, columnId) => {
          const a = (rowA.values[columnId] || "").toLowerCase();
          const b = (rowB.values[columnId] || "").toLowerCase();
          return a.localeCompare(b);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { original } = row;
          const serviceType = deriveServiceType(original);
          return (
            <div
              style={{
                maxWidth: "120px",
                margin: "0 auto",
                textAlign: "center",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {serviceType || ""}
            </div>
          );
        },
      },
      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>Report Type</span>
            </div>
            <select
              value={reportTypeFilter}
              onChange={(e) => { setReportTypeFilter(e.target.value); }}
              style={{
                fontSize: "12px",
                padding: "2px 6px",
                borderRadius: "6px",
                border: "1px solid #dde0e1",
                background: "#f8f9fa",
              }}
              title="Filter by Report Type"
            >
              <option value="">All</option>
              <option value="Total">Total</option>
              <option value="Compact">Compact</option>
              <option value="Personal">Personal</option>
              <option value="Not Available">Not Available</option>
            </select>
          </div>
        ),
        headerTitle: "Report Type",
        accessor: (row) => row.reportType || "",
        id: "reportTypeDisplay",
        disableSortBy: false,
        sortType: (rowA, rowB, columnId) => {
          const a = (rowA.values[columnId] || "").toLowerCase();
          const b = (rowB.values[columnId] || "").toLowerCase();
          return a.localeCompare(b);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { original } = row;
          return (
            <ReportTypeCell
              client={original}
              tick={refreshTick}
              onResolved={(resolved) => {
                // Persist the fetched reportType into table data for filtering/sorting/export
                setDataForTables((prev) =>
                  (prev || []).map((r) =>
                    r.org === original.org && r.center === original.center
                      ? { ...r, reportType: resolved }
                      : r
                  )
                );
              }}
            />
          );
        },
      },
      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>Report Add-ons</span>
            </div>
            <CustomFilterDropdown
              value={reportAddonsFilter}
              options={["", ...dynamicAddonOptions, "No Add-ons"]}
              onChange={(val) => setReportAddonsFilter(val)}
              title="Filter by Report Add-ons"
            />
          </div>
        ),
        headerTitle: "Report Add-ons",
        accessor: (row) => (deriveReportAddons(row) || []).join(", "),
        id: "reportAddonsDisplay",
        disableSortBy: false,
        sortType: (rowA, rowB, columnId) => {
          const a = (rowA.values[columnId] || "").toLowerCase();
          const b = (rowB.values[columnId] || "").toLowerCase();
          return a.localeCompare(b);
        },
        Cell: (cell) => {
          const { row } = cell;
          const { original } = row;
          const reportAddons = deriveReportAddons(original);
          return (
            <div
              style={{
                maxWidth: "180px",
                margin: "0 auto",
                textAlign: "left",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {reportAddons.join(", ") || ""}
            </div>
          );
        },
      },

      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="text-center">Plan Type</span>
            </div>
            <select
              value={planTypeFilter}
              onChange={(e) => setPlanTypeFilter(e.target.value)}
              style={{
                fontSize: "12px",
                padding: "2px 6px",
                borderRadius: "6px",
                border: "1px solid #dde0e1",
                background: "#f8f9fa",
              }}
              title="Filter by Plan Type"
            >
              <option value="">All</option>
              <option value="Starter">Starter</option>
              <option value="Growth">Growth</option>
              <option value="Enterprise">Enterprise</option>
              <option value="Not Available">Not Available</option>
            </select>
          </div>
        ),
        accessor: (row) => derivePlanType(row),
        id: "planTypeDisplay",
        disableSortBy: false,
        Cell: (cell) => {
          const { row } = cell;
          const { original } = row;
          const planType = derivePlanType(original);
          return (
            <div style={{ maxWidth: "120px", margin: "0 auto", textAlign: "center" }}>
              {planType || ""}
            </div>
          );
        },
      },
      {
        Header: () => (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span className="text-center">Client Type</span>
            </div>
            <select
              value={clientTypeFilter}
              onChange={(e) => setClientTypeFilter(e.target.value)}
              style={{
                fontSize: "12px",
                padding: "2px 6px",
                borderRadius: "6px",
                border: "1px solid #dde0e1",
                background: "#f8f9fa",
              }}
              title="Filter by Client Type"
            >
              <option value="">All</option>
              <option value="LAB">LAB</option>
              <option value="LIS">LIS</option>
              <option value="Not Available">Not Available</option>
            </select>
          </div>
        ),
        accessor: (row) => deriveClientType(row),
        id: "clientTypeDisplay",
        disableSortBy: false,
        Cell: (cell) => {
          const { row } = cell;
          const { original } = row;
          const clientType = deriveClientType(original);
          return (
            <div style={{ maxWidth: "100px", margin: "0 auto", textAlign: "center" }}>
              {clientType || ""}
            </div>
          );
        },
      },
      {
        Header: "Edit",
        accessor: "Edit",
        disableSortBy: true,
        Cell: (cell) => {
          const { row } = cell;
          const { values } = row;

          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                className="btn btn-primary btn-sm"
                style={{
                  whiteSpace: "nowrap",
                  padding: isMobile ? "4px 10px" : "6px 12px",
                }}
                onClick={() => {
                  const userId = getUserIdForClient(values);
                  if (userId) {
                    window.location.assign(`/client-subscription?userId=${userId}`);
                  } else {
                    alert.error('User ID not found for this client');
                  }
                }}
              >
                Edit
              </button>
            </div>
          );
        },
        showMobile: true,
      },
    ],
    [isMobile, reportTypeFilter, reportAddonsFilter, subscriptionStatusFilter, serviceTypeFilter, planTypeFilter, clientTypeFilter, invoiceAmounts, perReportRates, activityCounts, lisHisNameFilter, codebaseTagFilter, clientAlertFilter, alertsLoaded]
  );


  return (
    <>
      {pageLoader ? (
        <div className="d-flex align-items-center justify-content-center">
          <Spinner animation="border" />
        </div>
      ) : isInitialLoading ? (
        <LoadingScreen progress={loadingProgress} />
      ) : (
        <>
          {/* Count Cards */}
          <div className="kpi-cards-container" style={{ display: "flex", gap: "15px", marginBottom: "20px", flexWrap: "wrap", animation: "fadeIn 0.6s ease" }}>
            {/* Total Clients Card */}
            <div style={{
              flex: "1",
              minWidth: "180px",
              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)",
              backdropFilter: "blur(16px) saturate(180%)",
              WebkitBackdropFilter: "blur(16px) saturate(180%)",
              borderRadius: "12px",
              border: subscriptionStatusFilter === "" ? "3px solid rgba(139, 92, 246, 0.8)" : "1px solid rgba(255, 255, 255, 0.18)",
              padding: "12px 16px",
              boxShadow: subscriptionStatusFilter === "" ? "0 6px 25px rgba(99, 102, 241, 0.4)" : "0 4px 15px rgba(99, 102, 241, 0.25)",
              color: "#1e293b",
              position: "relative",
              overflow: "hidden",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "14px"
            }}
              onClick={() => setSubscriptionStatusFilter("")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px) scale(1.01)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(139, 92, 246, 0.25)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(59, 130, 246, 0.25) 100%)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = subscriptionStatusFilter === "" ? "0 6px 25px rgba(99, 102, 241, 0.4)" : "0 4px 15px rgba(139, 92, 246, 0.15)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)";
              }}
            >
              {/* Shine overlay */}
              <div style={{
                position: "absolute",
                top: "0",
                left: "-100%",
                width: "80px",
                height: "200%",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                transform: "skewX(-15deg)",
                transition: "left 0.6s",
                pointerEvents: "none"
              }} onMouseEnter={(e) => {
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  const overlay = parent.querySelector('div[style*="skewX"]');
                  if (overlay) {
                    overlay.style.left = "150%";
                    setTimeout(() => overlay.style.left = "-100%", 600);
                  }
                }
              }}></div>

              {/* Icon */}
              <div style={{
                width: "50px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                background: "rgba(139, 92, 246, 0.15)",
                fontSize: "28px",
                animation: "iconFloat 3s ease-in-out infinite",
                flexShrink: 0
              }}>👥</div>

              {/* Content */}
              <div style={{ flex: "1", minWidth: 0 }}>
                <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", opacity: "0.7", fontWeight: "800", marginBottom: "4px", color: "#000000" }} className="kpi-card-label">
                  {isMobile ? "Clients" : "Clients"}
                </div>
                <div style={{ fontSize: "28px", fontWeight: "800", lineHeight: "1", color: "#1e293b" }}>
                  {(reportTypeFilter || reportAddonsFilter || planTypeFilter || searchQuery) ? filteredDataForTables.length : visibleAllRows.length}
                </div>
              </div>
            </div>

            {/* Total Live Clients Card */}
            <div style={{
              flex: "1",
              minWidth: "180px",
              background: "linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(16, 185, 129, 0.3) 100%)",
              backdropFilter: "blur(16px) saturate(180%)",
              WebkitBackdropFilter: "blur(16px) saturate(180%)",
              borderRadius: "12px",
              border: subscriptionStatusFilter === "live" ? "3px solid rgba(34, 197, 94, 0.8)" : "1px solid rgba(255, 255, 255, 0.18)",
              padding: "12px 16px",
              boxShadow: subscriptionStatusFilter === "live" ? "0 6px 25px rgba(34, 197, 94, 0.45)" : "0 4px 15px rgba(34, 197, 94, 0.25)",
              color: "#1e293b",
              position: "relative",
              overflow: "hidden",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "14px"
            }}
              onClick={() => setSubscriptionStatusFilter("live")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px) scale(1.01)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(34, 197, 94, 0.35)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(34, 197, 94, 0.35) 0%, rgba(16, 185, 129, 0.35) 100%)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = subscriptionStatusFilter === "live" ? "0 6px 25px rgba(34, 197, 94, 0.45)" : "0 4px 15px rgba(34, 197, 94, 0.25)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(16, 185, 129, 0.3) 100%)";
              }}
            >
              {/* Shine overlay */}
              <div style={{
                position: "absolute",
                top: "0",
                left: "-100%",
                width: "80px",
                height: "200%",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                transform: "skewX(-15deg)",
                transition: "left 0.6s",
                pointerEvents: "none"
              }} onMouseEnter={(e) => {
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  const overlay = parent.querySelector('div[style*="skewX"]');
                  if (overlay) {
                    overlay.style.left = "150%";
                    setTimeout(() => overlay.style.left = "-100%", 600);
                  }
                }
              }}></div>

              {/* Icon */}
              <div style={{
                width: "50px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                background: "rgba(34, 197, 94, 0.2)",
                fontSize: "28px",
                animation: "iconFloat 3s ease-in-out infinite",
                flexShrink: 0
              }}>✅</div>

              {/* Content */}
              <div style={{ flex: "1", minWidth: 0 }}>
                <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", opacity: "0.7", fontWeight: "800", marginBottom: "4px", color: "#000000" }} className="kpi-card-label">
                  {isMobile ? "Live Clients" : "Live Clients"}
                </div>
                <div style={{ fontSize: "28px", fontWeight: "800", lineHeight: "1", color: "#1e293b" }}>
                  {((reportTypeFilter || reportAddonsFilter || planTypeFilter || searchQuery) ? filteredDataForTables : visibleAllRows)
                    .filter((each) => (each.subscriptionStatus || "").toString().trim().toLowerCase() === "live").length}
                </div>
              </div>
            </div>

            {/* Total Inactive Clients Card */}
            <div style={{
              flex: "1",
              minWidth: "180px",
              background: "linear-gradient(135deg, rgba(148, 163, 184, 0.3) 0%, rgba(100, 116, 139, 0.3) 100%)",
              backdropFilter: "blur(16px) saturate(180%)",
              WebkitBackdropFilter: "blur(16px) saturate(180%)",
              borderRadius: "12px",
              border: subscriptionStatusFilter === "inactive" ? "3px solid rgba(148, 163, 184, 0.8)" : "1px solid rgba(255, 255, 255, 0.18)",
              padding: "12px 16px",
              boxShadow: subscriptionStatusFilter === "inactive" ? "0 6px 25px rgba(148, 163, 184, 0.4)" : "0 4px 15px rgba(148, 163, 184, 0.25)",
              color: "#1e293b",
              position: "relative",
              overflow: "hidden",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "14px"
            }}
              onClick={() => setSubscriptionStatusFilter("inactive")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px) scale(1.01)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(100, 116, 139, 0.25)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(100, 116, 139, 0.25) 0%, rgba(71, 85, 105, 0.25) 100%)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = subscriptionStatusFilter === "inactive" ? "0 6px 25px rgba(148, 163, 184, 0.4)" : "0 4px 15px rgba(148, 163, 184, 0.25)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(148, 163, 184, 0.3) 0%, rgba(100, 116, 139, 0.3) 100%)";
              }}
            >
              {/* Shine overlay */}
              <div style={{
                position: "absolute",
                top: "0",
                left: "-100%",
                width: "80px",
                height: "200%",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                transform: "skewX(-15deg)",
                transition: "left 0.6s",
                pointerEvents: "none"
              }} onMouseEnter={(e) => {
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  const overlay = parent.querySelector('div[style*="skewX"]');
                  if (overlay) {
                    overlay.style.left = "150%";
                    setTimeout(() => overlay.style.left = "-100%", 600);
                  }
                }
              }}></div>

              {/* Icon */}
              <div style={{
                width: "50px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                background: "rgba(148, 163, 184, 0.2)",
                fontSize: "28px",
                animation: "iconFloat 3s ease-in-out infinite",
                flexShrink: 0
              }}>⏸️</div>

              {/* Content */}
              <div style={{ flex: "1", minWidth: 0 }}>
                <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", opacity: "0.7", marginBottom: "4px", fontWeight: "800", color: "#000000" }} className="kpi-card-label">
                  {isMobile ? "Inactive Clients" : "Inactive Clients"}
                </div>
                <div style={{ fontSize: "28px", fontWeight: "800", lineHeight: "1", color: "#1e293b" }}>
                  {((reportTypeFilter || reportAddonsFilter || planTypeFilter || searchQuery) ? filteredDataForTables : visibleAllRows)
                    .filter((each) => (each.subscriptionStatus || "").toString().trim().toLowerCase() === "inactive").length}
                </div>
              </div>
            </div>

            {/* Total On Hold Clients Card */}
            <div style={{
              flex: "1",
              minWidth: "180px",
              background: "linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.3) 100%)",
              backdropFilter: "blur(16px) saturate(180%)",
              WebkitBackdropFilter: "blur(16px) saturate(180%)",
              borderRadius: "12px",
              border: subscriptionStatusFilter === "on hold" ? "3px solid rgba(251, 191, 36, 0.8)" : "1px solid rgba(255, 255, 255, 0.18)",
              padding: "12px 16px",
              boxShadow: subscriptionStatusFilter === "on hold" ? "0 6px 25px rgba(251, 191, 36, 0.45)" : "0 4px 15px rgba(251, 191, 36, 0.25)",
              color: "#1e293b",
              position: "relative",
              overflow: "hidden",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "14px"
            }}
              onClick={() => setSubscriptionStatusFilter("on hold")}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px) scale(1.01)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(251, 191, 36, 0.35)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(251, 191, 36, 0.35) 0%, rgba(245, 158, 11, 0.35) 100%)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = subscriptionStatusFilter === "on hold" ? "0 6px 25px rgba(251, 191, 36, 0.45)" : "0 4px 15px rgba(251, 191, 36, 0.25)";
                e.currentTarget.style.background = "linear-gradient(135deg, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.3) 100%)";
              }}
            >
              {/* Shine overlay */}
              <div style={{
                position: "absolute",
                top: "0",
                left: "-100%",
                width: "80px",
                height: "200%",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
                transform: "skewX(-15deg)",
                transition: "left 0.6s",
                pointerEvents: "none"
              }} onMouseEnter={(e) => {
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  const overlay = parent.querySelector('div[style*="skewX"]');
                  if (overlay) {
                    overlay.style.left = "150%";
                    setTimeout(() => overlay.style.left = "-100%", 600);
                  }
                }
              }}></div>

              {/* Icon */}
              <div style={{
                width: "50px",
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "10px",
                background: "rgba(251, 191, 36, 0.2)",
                fontSize: "28px",
                animation: "iconFloat 3s ease-in-out infinite",
                flexShrink: 0
              }}>⏳</div>

              {/* Content */}
              <div style={{ flex: "1", minWidth: 0 }}>
                <div style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", opacity: "0.7", marginBottom: "4px", fontWeight: "800", color: "#000000" }} className="kpi-card-label">
                  {isMobile ? "On Hold Clients" : "On Hold Clients"}
                </div>
                <div style={{ fontSize: "28px", fontWeight: "800", lineHeight: "1", color: "#1e293b" }}>
                  {((reportTypeFilter || reportAddonsFilter || planTypeFilter || searchQuery) ? filteredDataForTables : visibleAllRows)
                    .filter((each) => (each.subscriptionStatus || "").toString().trim().toLowerCase() === "on hold" || (each.subscriptionStatus || "").toString().trim().toLowerCase() === "onhold").length}
                </div>
              </div>
            </div>
          </div>

          {/* Search and Export Bar */}
          <div className="d-flex justify-content-end align-items-center gap-2" style={{ marginBottom: "20px" }}>
            <div style={{ maxWidth: "300px", width: "100%" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search by client name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
            </div>
            <button
              type="button"
              className="btn btn-success btn-sm"
              title={isExporting ? "Exporting data..." : "Export all filtered clients to CSV"}
              onClick={exportTableToCSV}
              disabled={isExporting}
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                fontWeight: "500",
                minWidth: "140px", // Prevent button resize during loading
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px"
              }}
            >
              {isExporting ? (
                <>
                  <Spinner animation="border" size="sm" />
                  {exportProgress.total > 0 ? `Fetching ${exportProgress.current}/${exportProgress.total}...` : "Starting..."}
                </>
              ) : (
                "Export"
              )}
            </button>
          </div>

          <div
            className="table-view-container"
            ref={tableTopRef}
            style={{
              maxHeight: "calc(100vh - 280px)",
              minHeight: "500px",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              backgroundColor: "#fff"
            }}
          >
            <Table key={tableKey} columns={columns} data={paginatedDataForTables} isMobile={isMobile} />
          </div>

          <div
            className="d-flex align-items-stretch justify-content-between flex-wrap"
            style={{ gap: "12px", marginTop: "14px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 12px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, rgba(99, 102, 241, 0.10) 0%, rgba(59, 130, 246, 0.10) 100%)",
                border: "1px solid rgba(255, 255, 255, 0.4)",
                boxShadow: "0 6px 18px rgba(99, 102, 241, 0.08)",
                backdropFilter: "blur(10px) saturate(160%)",
                WebkitBackdropFilter: "blur(10px) saturate(160%)",
                minWidth: isMobile ? "100%" : "320px",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(99, 102, 241, 0.14)",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6H21" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8 12H21" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                  <path d="M8 18H21" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                  <path d="M3 6H4" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                  <path d="M3 12H4" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                  <path d="M3 18H4" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "12px", color: "#475569", fontWeight: 700, letterSpacing: "0.2px" }}>
                  {totalRows === 0
                    ? "0 results"
                    : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, totalRows)} of ${totalRows}`}
                </div>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                  {totalPages <= 1 ? "Page 1" : `Page ${currentPage} / ${totalPages}`}
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 12px",
                borderRadius: "12px",
                background: "rgba(255, 255, 255, 0.7)",
                border: "1px solid rgba(221, 224, 225, 0.9)",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.06)",
                backdropFilter: "blur(10px) saturate(160%)",
                WebkitBackdropFilter: "blur(10px) saturate(160%)",
                marginLeft: "auto",
                flexWrap: "wrap",
                justifyContent: isMobile ? "space-between" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <button
                type="button"
                className="btn btn-light"
                disabled={currentPage <= 1}
                onClick={() => handlePageChange(currentPage - 1)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "10px",
                  padding: "8px 12px",
                  border: "1px solid rgba(221, 224, 225, 1)",
                  background: currentPage <= 1 ? "rgba(243, 244, 246, 0.9)" : "rgba(255, 255, 255, 0.9)",
                  color: "#0f172a",
                  fontWeight: 700,
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>‹</span>
                Prev
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                {getPaginationModel().map((it) => {
                  if (it.type === "ellipsis") {
                    return (
                      <span
                        key={it.key}
                        style={{
                          padding: "0 6px",
                          color: "#94a3b8",
                          fontWeight: 800,
                          userSelect: "none",
                        }}
                        aria-hidden="true"
                      >
                        …
                      </span>
                    );
                  }

                  const active = it.page === currentPage;
                  return (
                    <button
                      key={it.key}
                      type="button"
                      onClick={() => handlePageChange(it.page)}
                      className="btn"
                      style={{
                        borderRadius: "10px",
                        padding: "8px 12px",
                        minWidth: "42px",
                        border: active ? "1px solid rgba(99, 102, 241, 0.45)" : "1px solid rgba(221, 224, 225, 1)",
                        background: active
                          ? "linear-gradient(135deg, rgba(99, 102, 241, 0.18) 0%, rgba(59, 130, 246, 0.16) 100%)"
                          : "rgba(255, 255, 255, 0.9)",
                        color: active ? "#3730a3" : "#0f172a",
                        fontWeight: 800,
                        boxShadow: active ? "0 8px 20px rgba(99, 102, 241, 0.16)" : "none",
                      }}
                      aria-current={active ? "page" : undefined}
                    >
                      {it.page}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                className="btn btn-light"
                disabled={currentPage >= totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "10px",
                  padding: "8px 12px",
                  border: "1px solid rgba(221, 224, 225, 1)",
                  background: currentPage >= totalPages ? "rgba(243, 244, 246, 0.9)" : "rgba(255, 255, 255, 0.9)",
                  color: "#0f172a",
                  fontWeight: 700,
                }}
              >
                Next
                <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>›</span>
              </button>
            </div>
          </div>

          {/* Modal For updating Client Data  */}
          <div>
            <>
              <Modal show={modalOpen} centered size="xl">
                <Modal.Body style={{ textAlign: "center" }}>
                  {/* <Spinner animation='border' size='sm' /> Loading Data ... */}
                  <div className="d-flex w-100 justify-content-end">
                    <button
                      type="button"
                      title="close modal"
                      style={{
                        height: "30px",
                        width: "30px",
                        borderRadius: "40px",
                        padding: "2px",
                      }}
                      className="mb-1 btn btn-danger"
                      aria-label="Close"
                      onClick={() => handleClose()}
                    >
                      X
                    </button>
                  </div>

                  {modalOpen && <Subscription />}
                </Modal.Body>
              </Modal>
            </>
          </div>
        </>
      )}
    </>
  );
};

export default ClientsTableView;
